<template>
  <section id="liquidity">
    <b-overlay :show="showLiqTrend" spinner-variant="primary" variant="transparent" blur="3px" rounded="sm">
      <b-card>
        <b-card-header>
          <h5>
            <feather-icon icon="TrendingUpIcon" size="20" class="mr-50" />
            Liquidity trend
          </h5>
          <p class="mb-0 text-muted">Track your liquidity forecast with bank account balance — all amounts gross incl. VAT, monthly Finanzamt VAT remittance, quarterly OSS VAT and permanent payments (manual entries) included as outflows</p>
          <b-popover target="popover-liquidity" triggers="hover" placement="bottom">
            <span>No data</span>
          </b-popover>
        </b-card-header>
        <b-card-body class="mt-1 pb-1">
          <div class="d-flex align-items-center flex-wrap" style="gap: 1.25rem;">
            <!-- Bank account -->
            <div class="d-flex align-items-center" style="gap: 0.5rem;">
              <small class="text-muted font-weight-bold text-nowrap">Bank account</small>
              <b-input-group style="width: 160px;">
                <b-input-group-prepend>
                  <b-input-group-text>
                    €
                  </b-input-group-text>
                </b-input-group-prepend>
                <b-form-input id="bank-account-statement" v-model="bankAccountStatement" placeholder="0" type="number"
                  @keyup.enter="changeBankAccountStatement" />
              </b-input-group>
            </div>
            <div class="text-muted" style="width: 1px; height: 28px; background: #e0e0e0;" />
            <!-- Buffer -->
            <div class="d-flex align-items-center" style="gap: 0.5rem;">
              <feather-icon icon="ShieldIcon" size="14" class="text-muted" />
              <small class="text-muted font-weight-bold text-nowrap">Buffer</small>
              <b-form-input id="liquidity-buffer" v-model="liquidityBuffer" placeholder="0" type="number"
                style="width: 110px;" @change="changeBuffer" @keyup.enter="changeBuffer" />
            </div>
            <div class="text-muted" style="width: 1px; height: 28px; background: #e0e0e0;" />
            <!-- Expected incoming orders -->
            <div class="d-flex align-items-center" style="gap: 0.5rem;">
              <small class="text-muted font-weight-bold text-nowrap">Expected orders</small>
              <b-form-checkbox v-model="showExpectedOrders" switch class="mb-0" @change="toggleExpectedOrders" />
              <b-input-group style="width: 110px;" :class="{ 'opacity-50': !showExpectedOrders }">
                <b-form-input id="expected-orders-pct" v-model="expectedOrdersPct" placeholder="50" type="number"
                  min="0" max="200" :disabled="!showExpectedOrders" @change="changeExpectedOrdersPct"
                  @keyup.enter="changeExpectedOrdersPct" />
                <b-input-group-append>
                  <b-input-group-text>%</b-input-group-text>
                </b-input-group-append>
              </b-input-group>
              <small class="text-muted text-nowrap">of last year</small>
            </div>
            <div class="text-muted" style="width: 1px; height: 28px; background: #e0e0e0;" />
            <!-- Scenario -->
            <div class="d-flex flex-column" style="gap: 0.25rem;">
              <div class="d-flex align-items-center" style="gap: 0.5rem;">
                <feather-icon icon="SlidersIcon" size="14" class="text-muted" />
                <small class="text-muted font-weight-bold text-nowrap">Scenario</small>
                <b-button v-b-tooltip.hover :title="scenarioHints.base" size="sm"
                  :variant="scenario === 'base' ? 'primary' : 'flat-primary'"
                  @click="setScenario('base')">Base</b-button>
                <b-button v-b-tooltip.hover :title="scenarioHints.conservative" size="sm"
                  :variant="scenario === 'conservative' ? 'warning' : 'flat-warning'"
                  @click="setScenario('conservative')">Conservative</b-button>
                <b-button v-b-tooltip.hover :title="scenarioHints.stress" size="sm"
                  :variant="scenario === 'stress' ? 'danger' : 'flat-danger'"
                  @click="setScenario('stress')">Stress</b-button>
              </div>
              <small class="text-muted d-block" style="max-width: 520px;">
                <feather-icon icon="InfoIcon" size="12" class="mr-25" />{{ scenarioHints[scenario] }}
              </small>
            </div>
          </div>
        </b-card-body>
        <b-card-body>
          <b-row>
            <b-col xl="4" md="4" class="mb-3">
              <div class="modern-stat-card" style="cursor: pointer;" @click="showCardDetail(7, 'days', 'Next 7 Days')">
                <div class="stat-icon-wrapper gradient-primary">
                  <feather-icon icon="TrendingUpIcon" size="28" />
                </div>
                <div class="stat-content">
                  <div class="stat-header">
                    <h5 class="stat-title">Next 7 Days</h5>
                  </div>
                  <div class="stat-value-wrapper">
                    <h2 v-if="liq7DaysTrend >= 0" class="stat-main-value text-success">
                      +€ {{ liq7DaysTrend | formatNumber }}
                    </h2>
                    <h2 v-else class="stat-main-value text-danger">
                      €{{ liq7DaysTrend | formatNumber }}
                    </h2>
                    <div v-if="showExpectedOrders" class="stat-change text-info font-weight-bold">
                      incl. expected orders: €{{ liq7DaysTrendWithOrders | formatNumber }}
                    </div>
                    <b-badge v-if="isBelowBuffer(liq7DaysTrend)" variant="light-danger" pill class="mt-50">
                      <feather-icon icon="AlertTriangleIcon" size="12" class="mr-25" />Below buffer
                    </b-badge>
                  </div>
                </div>
              </div>
            </b-col>
            <b-col xl="4" md="4" class="mb-3">
              <div class="modern-stat-card" style="cursor: pointer;"
                @click="showCardDetail(14, 'days', 'Next 14 Days')">
                <div class="stat-icon-wrapper gradient-info">
                  <feather-icon icon="TrendingUpIcon" size="28" />
                </div>
                <div class="stat-content">
                  <div class="stat-header">
                    <h5 class="stat-title">Next 14 Days</h5>
                  </div>
                  <div class="stat-value-wrapper">
                    <h2 v-if="liq14DaysTrend >= 0" class="stat-main-value text-success">
                      +€ {{ liq14DaysTrend | formatNumber }}
                    </h2>
                    <h2 v-else class="stat-main-value text-danger">
                      €{{ liq14DaysTrend | formatNumber }}
                    </h2>
                    <div v-if="showExpectedOrders" class="stat-change text-info font-weight-bold">
                      incl. expected orders: €{{ liq14DaysTrendWithOrders | formatNumber }}
                    </div>
                    <b-badge v-if="isBelowBuffer(liq14DaysTrend)" variant="light-danger" pill class="mt-50">
                      <feather-icon icon="AlertTriangleIcon" size="12" class="mr-25" />Below buffer
                    </b-badge>
                  </div>
                </div>
              </div>
            </b-col>
            <b-col xl="4" md="4" class="mb-3">
              <div class="modern-stat-card" style="cursor: pointer;"
                @click="showCardDetail(21, 'days', 'Next 21 Days')">
                <div class="stat-icon-wrapper gradient-warning">
                  <feather-icon icon="TrendingUpIcon" size="28" />
                </div>
                <div class="stat-content">
                  <div class="stat-header">
                    <h5 class="stat-title">Next 21 Days</h5>
                  </div>
                  <div class="stat-value-wrapper">
                    <h2 v-if="liq21DaysTrend >= 0" class="stat-main-value text-success">
                      +€ {{ liq21DaysTrend | formatNumber }}
                    </h2>
                    <h2 v-else class="stat-main-value text-danger">
                      €{{ liq21DaysTrend | formatNumber }}
                    </h2>
                    <div v-if="showExpectedOrders" class="stat-change text-info font-weight-bold">
                      incl. expected orders: €{{ liq21DaysTrendWithOrders | formatNumber }}
                    </div>
                    <b-badge v-if="isBelowBuffer(liq21DaysTrend)" variant="light-danger" pill class="mt-50">
                      <feather-icon icon="AlertTriangleIcon" size="12" class="mr-25" />Below buffer
                    </b-badge>
                  </div>
                </div>
              </div>
            </b-col>
            <b-col xl="4" md="4" class="mb-3">
              <div class="modern-stat-card" style="cursor: pointer;"
                @click="showCardDetail(2, 'months', 'Next 2 Months')">
                <div class="stat-icon-wrapper gradient-danger">
                  <feather-icon icon="TrendingUpIcon" size="28" />
                </div>
                <div class="stat-content">
                  <div class="stat-header">
                    <h5 class="stat-title">Next 2 Months</h5>
                  </div>
                  <div class="stat-value-wrapper">
                    <h2 v-if="liq2MonthsTrend >= 0" class="stat-main-value text-success">
                      +€ {{ liq2MonthsTrend | formatNumber }}
                    </h2>
                    <h2 v-else class="stat-main-value text-danger">
                      €{{ liq2MonthsTrend | formatNumber }}
                    </h2>
                    <div v-if="showExpectedOrders" class="stat-change text-info font-weight-bold">
                      incl. expected orders: €{{ liq2MonthsTrendWithOrders | formatNumber }}
                    </div>
                    <b-badge v-if="isBelowBuffer(liq2MonthsTrend)" variant="light-danger" pill class="mt-50">
                      <feather-icon icon="AlertTriangleIcon" size="12" class="mr-25" />Below buffer
                    </b-badge>
                  </div>
                </div>
              </div>
            </b-col>
            <b-col xl="4" md="4" class="mb-3">
              <div class="modern-stat-card" style="cursor: pointer;"
                @click="showCardDetail(3, 'months', 'Next 3 Months')">
                <div class="stat-icon-wrapper gradient-success">
                  <feather-icon icon="TrendingUpIcon" size="28" />
                </div>
                <div class="stat-content">
                  <div class="stat-header">
                    <h5 class="stat-title">Next 3 Months</h5>
                  </div>
                  <div class="stat-value-wrapper">
                    <h2 v-if="liq3MonthsTrend >= 0" class="stat-main-value text-success">
                      +€ {{ liq3MonthsTrend | formatNumber }}
                    </h2>
                    <h2 v-else class="stat-main-value text-danger">
                      €{{ liq3MonthsTrend | formatNumber }}
                    </h2>
                    <div v-if="showExpectedOrders" class="stat-change text-info font-weight-bold">
                      incl. expected orders: €{{ liq3MonthsTrendWithOrders | formatNumber }}
                    </div>
                    <b-badge v-if="isBelowBuffer(liq3MonthsTrend)" variant="light-danger" pill class="mt-50">
                      <feather-icon icon="AlertTriangleIcon" size="12" class="mr-25" />Below buffer
                    </b-badge>
                  </div>
                </div>
              </div>
            </b-col>
          </b-row>
        </b-card-body>
        <b-card-body>
          <ECharts :theme="echartsTheme" ref="liqTrendChart" :options="liqTrendChart"
            style="width: 100%; height: 500px;" />
        </b-card-body>
        <b-card-body class="card-body-table">
          <b-table striped hover responsive class="position-relative" :current-page="liqTrendTable.currentPage"
            :per-page="10" :items="liqTrendTable.items" :fields="liqTrendTable.fields"
            :sort-by.sync="liqTrendTable.sortBy" :sort-desc.sync="liqTrendTable.sortDesc"
            :sort-direction="liqTrendTable.sortDirection" :filter="liqTrendTable.filter"
            :filter-included-fields="liqTrendTable.filterOn">
          </b-table>
        </b-card-body>
        <b-card-body>
          <div class="pagination-wrapper">
            <b-pagination v-model="liqTrendTable.currentPage" :total-rows="liqTrendTable.totalRows" first-number
              last-number prev-class="prev-item" next-class="next-item" class="mb-0">
              <template #prev-text>
                <feather-icon icon="ChevronLeftIcon" size="18" />
              </template>
              <template #next-text>
                <feather-icon icon="ChevronRightIcon" size="18" />
              </template>
            </b-pagination>
          </div>
        </b-card-body>
      </b-card>
    </b-overlay>
    <b-modal v-model="cardDetail.show" :title="cardDetail.title" size="xl" hide-footer scrollable>
      <div class="mb-2">
        <b-row>
          <b-col md="3">
            <small class="text-muted d-block">Bank balance (start)</small>
            <strong>€ {{ cardDetail.startBalance | formatNumber }}</strong>
          </b-col>
          <b-col md="3">
            <small class="text-muted d-block">Total expected in</small>
            <strong class="text-success">€ {{ cardDetail.totalIn | formatNumber }}</strong>
          </b-col>
          <b-col md="3">
            <small class="text-muted d-block">Total expected out</small>
            <strong class="text-danger">€ {{ cardDetail.totalOut | formatNumber }}</strong>
          </b-col>
          <b-col md="3">
            <small class="text-muted d-block">Projected balance (end)</small>
            <strong :class="cardDetail.endBalance >= 0 ? 'text-success' : 'text-danger'">
              € {{ cardDetail.endBalance | formatNumber }}
            </strong>
          </b-col>
        </b-row>
      </div>
      <p class="text-muted small">
        Each row is one document (customer invoice, supplier bill, or open order) due in this
        period. <em>Net</em> is its effect on cash (supplier bills are negative);
        <em>Balance</em> is the running bank balance after it. The final <em>Balance</em> equals
        the card value. Vorkasse inflows are counted at 85%.
      </p>
      <b-table small striped hover responsive :items="cardDetail.rows" :fields="cardDetail.fields" />
      <p v-if="!cardDetail.rows.length" class="text-center text-muted my-2">
        No cash movements due in this period.
      </p>
    </b-modal>
    <b-row>
      <b-col xl="6" md="6">
        <b-overlay :show="showInPayments" spinner-variant="primary" variant="transparent" blur="3px" rounded="sm">
          <b-card>
            <b-card-header>
              <h5>
                <feather-icon icon="ArrowDownCircleIcon" size="20" class="mr-50" />
                Expected incoming payments
              </h5>
              <p class="mb-0 text-muted">Upcoming payments expected to be received</p>
              <h4 class="mt-50 mb-0 text-success">
                Total: € {{ totalInPayments | formatNumber }}
              </h4>
              <b-popover target="popover-expected-incoming-payments" triggers="hover" placement="bottom">
                <span>No data</span>
              </b-popover>
            </b-card-header>
            <b-card-body class="card-body-table">
              <b-table striped hover responsive class="position-relative" :current-page="inPaymentsTable.currentPage"
                :items="inPaymentsTable.items" :fields="inPaymentsTable.fields" :sort-by.sync="inPaymentsTable.sortBy"
                :sort-desc.sync="inPaymentsTable.sortDesc" :sort-direction="inPaymentsTable.sortDirection"
                :filter="inPaymentsTable.filter" :filter-included-fields="inPaymentsTable.filterOn"
                @sort-changed="sortChangedInPayments" />
            </b-card-body>
            <b-card-body>
              <div class="pagination-wrapper">
                <b-pagination v-model="inPaymentsTable.currentPage" :total-rows="inPaymentsTable.totalRows" first-number
                  last-number prev-class="prev-item" next-class="next-item" class="mb-0"
                  @change="handleInPaymentsTablePageChange">
                  <template #prev-text>
                    <feather-icon icon="ChevronLeftIcon" size="18" />
                  </template>
                  <template #next-text>
                    <feather-icon icon="ChevronRightIcon" size="18" />
                  </template>
                </b-pagination>
              </div>
            </b-card-body>
          </b-card>
        </b-overlay>
      </b-col>
      <b-col xl="6" md="6">
        <b-overlay :show="showOutPayments" spinner-variant="primary" variant="transparent" blur="3px" rounded="sm">
          <b-card>
            <b-card-header>
              <h5>
                <feather-icon icon="ArrowUpCircleIcon" size="20" class="mr-50" />
                Expected outgoing payments
              </h5>
              <p class="mb-0 text-muted">Upcoming payments expected to be made</p>
              <h4 class="mt-50 mb-0 text-danger">
                Total: € {{ totalOutPayments | formatNumber }}
              </h4>
              <b-popover target="popover-expected-outgoing-payments" triggers="hover" placement="bottom">
                <span>No data</span>
              </b-popover>
            </b-card-header>
            <b-card-body class="mt-1">
              <b-button v-ripple.400="'rgba(255, 255, 255, 0.15)'" variant="primary" class="btn-block"
                @click="openPermanentPaymentsDetails()">
                Permanent Payments
              </b-button>
            </b-card-body>
            <b-card-body class="card-body-table">
              <b-table striped hover responsive class="position-relative" :current-page="outPaymentsTable.currentPage"
                :items="outPaymentsTable.items" :fields="outPaymentsTable.fields"
                :sort-by.sync="outPaymentsTable.sortBy" :sort-desc.sync="outPaymentsTable.sortDesc"
                :sort-direction="outPaymentsTable.sortDirection" :filter="outPaymentsTable.filter"
                :filter-included-fields="outPaymentsTable.filterOn" @sort-changed="sortChangedOutPayments" />
            </b-card-body>
            <b-card-body>
              <div class="pagination-wrapper">
                <b-pagination v-model="outPaymentsTable.currentPage" :total-rows="outPaymentsTable.totalRows"
                  first-number last-number prev-class="prev-item" next-class="next-item" class="mb-0"
                  @change="handleOutPaymentsTablePageChange">
                  <template #prev-text>
                    <feather-icon icon="ChevronLeftIcon" size="18" />
                  </template>
                  <template #next-text>
                    <feather-icon icon="ChevronRightIcon" size="18" />
                  </template>
                </b-pagination>
              </div>
            </b-card-body>
          </b-card>
        </b-overlay>
      </b-col>
    </b-row>
    <b-modal ref="permanent-payments-modal" size="xl" title="Permanent payments" cancel-title="Close"
      cancel-variant="outline-secondary" :no-close-on-backdrop="true">
      <b-table striped hover responsive class="position-relative" :current-page="permanentPaymentsTable.currentPage"
        :items="permanentPaymentsTable.items" :fields="permanentPaymentsTable.fields"
        :sort-by.sync="permanentPaymentsTable.sortBy" :sort-desc.sync="permanentPaymentsTable.sortDesc"
        :sort-direction="permanentPaymentsTable.sortDirection" :filter="permanentPaymentsTable.filter"
        :filter-included-fields="permanentPaymentsTable.filterOn" @row-clicked="permanentPaymentsRowClicked">
        <template #cell(is_avg_payment)="data">
          <div v-if="data.item.is_avg_payment">
            <feather-icon icon="CheckCircleIcon" />
          </div>
          <div v-else>
            <feather-icon icon="XCircleIcon" />
          </div>
        </template>
        <template #cell(delete)="data">
          <b-button v-ripple.400="'rgba(255, 255, 255, 0.15)'" variant="flat" class="btn-icon"
            @click="openDeleteConfirm(data.item.id)">
            <feather-icon icon="Trash2Icon" />
          </b-button>
        </template>
      </b-table>
      <b-card-body class="d-flex justify-content-between flex-wrap pt-0">
        <!-- pagination -->
        <div>
          <b-pagination v-model="permanentPaymentsTable.currentPage" :total-rows="permanentPaymentsTable.totalRows"
            :per-page="100" first-number last-number prev-class="prev-item" next-class="next-item" class="mb-0"
            @change="handlePermanentPaymentsPageChange">
            <template #prev-text>
              <feather-icon icon="ChevronLeftIcon" size="18" />
            </template>
            <template #next-text>
              <feather-icon icon="ChevronRightIcon" size="18" />
            </template>
          </b-pagination>
        </div>
      </b-card-body>
      <b-row>
        <b-col xl="8" md="8">
        </b-col>
        <b-col xl="4" md="4">
          <b-button v-ripple.400="'rgba(255, 255, 255, 0.15)'" variant="primary" class="btn-block"
            @click="addPermanentPaymentClicked">
            Add Permanent Payment
          </b-button>
        </b-col>
      </b-row>
    </b-modal>
    <b-modal ref="modal-permanent-payment-submit" cancel-variant="outline-secondary" ok-title="Submit"
      cancel-title="Close" centered :title="permanentPaymentID ? 'Update' : 'Add'" :no-close-on-backdrop="true"
      @ok="handlePermanentPaymentSubmit" @hidden="resetPermanentPaymentModal">
      <b-form @submit.prevent>
        <b-form-group label="Name" label-for="permanentPaymentName">
          <b-form-input id="permanentPaymentName" v-model="permanentPaymentName" />
        </b-form-group>
        <b-form-group label="Recurring Type" label-for="permanentPaymentType">
          <v-select id="permanentPaymentType" v-model="permanentPaymentType" label="title"
            :options="permanentPaymentTypes" />
        </b-form-group>
        <b-form-group label="Value" label-for="permanentPaymentValue">
          <b-form-input id="permanentPaymentValue" v-model="permanentPaymentValue" type="number" />
        </b-form-group>
        <b-form-group label="Start Date Period" label-for="permanentPaymentStartDate">
          <b-input-group class="input-group-merge" style="width: 250px;">
            <b-input-group-prepend is-text>
              <feather-icon icon="CalendarIcon" />
            </b-input-group-prepend>
            <flat-pickr id="permanentPaymentStartDate" v-model="permanentPaymentStartDate" class="form-control" />
          </b-input-group>
        </b-form-group>
        <b-form-group label="End Date Period" label-for="permanentPaymentEndDate">
          <b-input-group class="input-group-merge" style="width: 250px;">
            <b-input-group-prepend is-text>
              <feather-icon icon="CalendarIcon" />
            </b-input-group-prepend>
            <flat-pickr id="permanentPaymentEndDate" v-model="permanentPaymentEndDate" class="form-control" />
          </b-input-group>
        </b-form-group>
        <b-form-group label="Payment Term" label-for="permanentPaymentDate">
          <b-input-group class="input-group-merge" style="width: 250px;">
            <b-input-group-prepend is-text>
              <feather-icon icon="CalendarIcon" />
            </b-input-group-prepend>
            <flat-pickr id="permanentPaymentDate" v-model="permanentPaymentDate" class="form-control" />
          </b-input-group>
        </b-form-group>
        <b-form-group label="Avg. Payment" label-for="is-avg-payment">
          <b-form-checkbox id="is-avg-payment" v-model="isAvgPayment">
          </b-form-checkbox>
        </b-form-group>
      </b-form>
    </b-modal>
    <b-card>
      <b-card-header>
        <h5>
          <feather-icon icon="ActivityIcon" size="20" class="mr-50" />
          Liquidity development
        </h5>
        <p class="mb-0 text-muted">Historical liquidity trends over selected date range</p>
        <b-popover target="popover-liquidity-development" triggers="hover" placement="bottom">
          <span>No data</span>
        </b-popover>
      </b-card-header>
      <b-card-body class="mt-1">
        <b-form-group label="Date Range" label-for="liquidity-dev-date-range">
          <b-input-group style="max-width: 250px;">
            <b-input-group-prepend>
              <b-input-group-text>
                <feather-icon icon="CalendarIcon" size="14" />
              </b-input-group-text>
            </b-input-group-prepend>
            <flat-pickr id="liquidity-dev-date-range" v-model="dateRange" placeholder="Date Range" class="form-control"
              :config="{ mode: 'range' }" @on-change="changeDateRange" />
          </b-input-group>
        </b-form-group>
      </b-card-body>
      <b-card-body>
        <ECharts :theme="echartsTheme" ref="liquidityDevelopmentChart" :options="liquidityDevelopmentChart"
          style="width: 100%; height: 500px;" />
      </b-card-body>
    </b-card>
    <b-overlay :show="showOrdersCancellationRate" spinner-variant="primary" variant="transparent" blur="3px"
      rounded="sm">
      <b-row>
        <b-col xl="6" md="6">
          <b-card>
            <b-card-header>
              <h5>
                <feather-icon icon="ShoppingCartIcon" size="20" class="mr-50" />
                Orders count
              </h5>
              <p class="mb-0 text-muted">Number of orders over selected date range</p>
              <b-popover target="popover-orders-count-development" triggers="hover" placement="bottom">
                <span>No data</span>
              </b-popover>
            </b-card-header>
            <b-card-body class="mt-1">
              <b-form-group label="Date Range" label-for="orders-count-date-range">
                <b-input-group style="max-width: 250px;">
                  <b-input-group-prepend>
                    <b-input-group-text>
                      <feather-icon icon="CalendarIcon" size="14" />
                    </b-input-group-text>
                  </b-input-group-prepend>
                  <flat-pickr id="orders-count-date-range" v-model="dateRange" placeholder="Date Range"
                    class="form-control" :config="{ mode: 'range' }" @on-change="changeDateRange" />
                </b-input-group>
              </b-form-group>
            </b-card-body>
            <b-card-body>
              <ECharts :theme="echartsTheme" ref="ordersCountDevelopmentChart" :options="ordersCountDevelopmentChart"
                style="width: 100%; height: 500px;" />
            </b-card-body>
          </b-card>
        </b-col>
        <b-col xl="6" md="6">
          <b-card>
            <b-card-header>
              <h5>
                <feather-icon icon="ListIcon" size="20" class="mr-50" />
                Orders Count Details
              </h5>
              <p class="mb-0 text-muted">Monthly breakdown of order counts</p>
            </b-card-header>
            <b-card-body class="card-body-table">
              <b-table striped hover responsive class="position-relative" :current-page="ordersCountTable.currentPage"
                :per-page="10" :items="ordersCountTable.items" :fields="ordersCountTable.fields"
                :sort-by.sync="ordersCountTable.sortBy" :sort-desc.sync="ordersCountTable.sortDesc"
                :sort-direction="ordersCountTable.sortDirection" :filter="ordersCountTable.filter"
                :filter-included-fields="ordersCountTable.filterOn" />
            </b-card-body>
            <b-card-body>
              <div class="pagination-wrapper">
                <b-pagination v-model="ordersCountTable.currentPage" :total-rows="ordersCountTable.totalRows"
                  first-number last-number prev-class="prev-item" next-class="next-item" class="mb-0">
                  <template #prev-text>
                    <feather-icon icon="ChevronLeftIcon" size="18" />
                  </template>
                  <template #next-text>
                    <feather-icon icon="ChevronRightIcon" size="18" />
                  </template>
                </b-pagination>
              </div>
            </b-card-body>
          </b-card>
        </b-col>
      </b-row>
      <b-row>
        <b-col xl="6" md="6">
          <b-card>
            <b-card-header>
              <h5>
                <feather-icon icon="XCircleIcon" size="20" class="mr-50" />
                Cancellation rate
              </h5>
              <p class="mb-0 text-muted">Order cancellation rate over selected date range</p>
              <b-popover target="popover-cancellation-rate-development" triggers="hover" placement="bottom">
                <span>No data</span>
              </b-popover>
            </b-card-header>
            <b-card-body class="mt-1">
              <b-form-group label="Date Range" label-for="cancellation-rate-date-range">
                <b-input-group style="max-width: 250px;">
                  <b-input-group-prepend>
                    <b-input-group-text>
                      <feather-icon icon="CalendarIcon" size="14" />
                    </b-input-group-text>
                  </b-input-group-prepend>
                  <flat-pickr id="cancellation-rate-date-range" v-model="dateRange" placeholder="Date Range"
                    class="form-control" :config="{ mode: 'range' }" @on-change="changeDateRange" />
                </b-input-group>
              </b-form-group>
            </b-card-body>
            <b-card-body>
              <ECharts :theme="echartsTheme" ref="cancellationRateDevelopmentChart"
                :options="cancellationRateDevelopmentChart" style="width: 100%; height: 500px;" />
            </b-card-body>
          </b-card>
        </b-col>
        <b-col xl="6" md="6">
          <b-card>
            <b-card-header>
              <h5>
                <feather-icon icon="BarChart2Icon" size="20" class="mr-50" />
                Cancellation rate by order value
              </h5>
              <p class="mb-0 text-muted">Cancellation rate segmented by order value ranges</p>
              <b-popover target="popover-cancellation-rate-order-value-development" triggers="hover" placement="bottom">
                <span>No data</span>
              </b-popover>
            </b-card-header>
            <b-card-body class="mt-1">
              <b-form-group label="Date Range" label-for="cancellation-rate-order-value-date-range">
                <b-input-group style="max-width: 250px;">
                  <b-input-group-prepend>
                    <b-input-group-text>
                      <feather-icon icon="CalendarIcon" size="14" />
                    </b-input-group-text>
                  </b-input-group-prepend>
                  <flat-pickr id="cancellation-rate-order-value-date-range" v-model="dateRange" placeholder="Date Range"
                    class="form-control" :config="{ mode: 'range' }" @on-change="changeDateRange" />
                </b-input-group>
              </b-form-group>
            </b-card-body>
            <b-card-body>
              <ECharts :theme="echartsTheme" ref="cancellationRateOrderValueDevelopmentChart"
                :options="cancellationRateOrderValueDevelopmentChart" style="width: 100%; height: 500px;" />
            </b-card-body>
          </b-card>
        </b-col>
      </b-row>
      <b-row>
        <b-col xl="6" md="6">
          <b-card no-body>
            <b-card-header>
              <h4 class="mb-0">
                Top cancelled products
                <feather-icon icon="InfoIcon" size="21" class="text-muted cursor-pointer"
                  id="popover-cancelled-productst" />
              </h4>
              <b-popover target="popover-cancelled-productst" triggers="hover" placement="bottom">
                <span>No data</span>
              </b-popover>
              <b-card-text class="font-medium-5 mb-0">
                <b-input-group class="input-group-merge" style="width: 250px;">
                  <b-input-group-prepend is-text>
                    <feather-icon icon="CalendarIcon" />
                  </b-input-group-prepend>
                  <flat-pickr v-model="dateRange" placeholder="Date Range" class="form-control"
                    :config="{ mode: 'range' }" @on-change="changeDateRange" />
                </b-input-group>
              </b-card-text>
            </b-card-header>
            <b-table striped hover responsive class="position-relative"
              :current-page="canceledProductsTable.currentPage" :per-page="10" :items="canceledProductsTable.items"
              :fields="canceledProductsTable.fields" :sort-by.sync="canceledProductsTable.sortBy"
              :sort-desc.sync="canceledProductsTable.sortDesc" :sort-direction="canceledProductsTable.sortDirection"
              :filter="canceledProductsTable.filter" :filter-included-fields="canceledProductsTable.filterOn">
            </b-table>
            <b-card-body class="d-flex justify-content-between flex-wrap pt-0">
              <!-- pagination -->
              <div>
                <b-pagination v-model="canceledProductsTable.currentPage" :total-rows="canceledProductsTable.totalRows"
                  first-number last-number prev-class="prev-item" next-class="next-item" class="mb-0">
                  <template #prev-text>
                    <feather-icon icon="ChevronLeftIcon" size="18" />
                  </template>
                  <template #next-text>
                    <feather-icon icon="ChevronRightIcon" size="18" />
                  </template>
                </b-pagination>
              </div>
            </b-card-body>
          </b-card>
        </b-col>
        <b-col xl="6" md="6">
          <b-card no-body>
            <b-card-header>
              <h4 class="mb-0">
                Avg. order value of cancelled orders
                <feather-icon icon="InfoIcon" size="21" class="text-muted cursor-pointer"
                  id="popover-avg-cancelled-order-value-development" />
              </h4>
              <b-popover target="popover-avg-cancelled-order-value-development" triggers="hover" placement="bottom">
                <span>No data</span>
              </b-popover>
              <b-card-text class="font-medium-5 mb-0">
                <b-input-group class="input-group-merge" style="width: 250px;">
                  <b-input-group-prepend is-text>
                    <feather-icon icon="CalendarIcon" />
                  </b-input-group-prepend>
                  <flat-pickr v-model="dateRange" placeholder="Date Range" class="form-control"
                    :config="{ mode: 'range' }" @on-change="changeDateRange" />
                </b-input-group>
              </b-card-text>
            </b-card-header>
            <ECharts :theme="echartsTheme" ref="avgCancelledOrderValueDevelopmentChart"
              :options="avgCancelledOrderValueDevelopmentChart" style="width: 100%; height: 500px;" />
          </b-card>
        </b-col>
      </b-row>
      <b-row>
        <b-col xl="6" md="6">
          <b-card no-body>
            <b-card-header>
              <h4 class="mb-0">
                Cancellation rate by customer group
                <feather-icon icon="InfoIcon" size="21" class="text-muted cursor-pointer"
                  id="popover-cancellation-rate-customer-group" />
              </h4>
              <b-popover target="popover-cancellation-rate-customer-group" triggers="hover" placement="bottom">
                <span>No data</span>
              </b-popover>
              <b-card-text class="font-medium-5 mb-0">
                <b-input-group class="input-group-merge" style="width: 250px;">
                  <b-input-group-prepend is-text>
                    <feather-icon icon="CalendarIcon" />
                  </b-input-group-prepend>
                  <flat-pickr v-model="dateRange" placeholder="Date Range" class="form-control"
                    :config="{ mode: 'range' }" @on-change="changeDateRange" />
                </b-input-group>
              </b-card-text>
            </b-card-header>
            <ECharts :theme="echartsTheme" ref="cancellationRateCustomerGroupDevelopmentChart"
              :options="cancellationRateCustomerGroupDevelopmentChart" style="width: 100%; height: 500px;" />
          </b-card>
        </b-col>
        <b-col xl="6">
          <b-card no-body>
            <b-card-header>
              <h4 class="mb-0">
                Days from order create to payment
                <feather-icon icon="InfoIcon" size="21" class="text-muted cursor-pointer"
                  id="popover-payment-days-distribution" />
              </h4>
              <b-popover target="popover-payment-days-distribution" triggers="hover" placement="bottom">
                <span>No data</span>
              </b-popover>
              <b-card-text class="font-medium-5 mb-0">
                <b-input-group class="input-group-merge" style="width: 250px;">
                  <b-input-group-prepend is-text>
                    <feather-icon icon="CalendarIcon" />
                  </b-input-group-prepend>
                  <flat-pickr v-model="dateRange" placeholder="Date Range" class="form-control"
                    :config="{ mode: 'range' }" @on-change="changeDateRange" />
                </b-input-group>
              </b-card-text>
            </b-card-header>
            <ECharts :theme="echartsTheme" ref="paymentDaysDistributionChart" :options="paymentDaysDistributionChart"
              style="width: 100%; height: 500px;" />
          </b-card>
        </b-col>
      </b-row>
      <b-row>
        <b-col xl="7" md="7">
          <b-card no-body>
            <b-card-header>
              <h4 class="mb-0">
                Payment methods distribution
                <feather-icon icon="InfoIcon" size="21" class="text-muted cursor-pointer"
                  id="popover-payment-methods-distribution" />
              </h4>
              <b-popover target="popover-payment-methods-distribution" triggers="hover" placement="bottom">
                <span>No data</span>
              </b-popover>
              <b-card-text class="font-medium-5 mb-0">
                <b-input-group class="input-group-merge" style="width: 250px;">
                  <b-input-group-prepend is-text>
                    <feather-icon icon="CalendarIcon" />
                  </b-input-group-prepend>
                  <flat-pickr v-model="dateRange" placeholder="Date Range" class="form-control"
                    :config="{ mode: 'range' }" @on-change="changeDateRange" />
                </b-input-group>
              </b-card-text>
            </b-card-header>
            <ECharts :theme="echartsTheme" ref="paymentMethodsDistributionChart"
              :options="paymentMethodsDistributionChart" style="width: 100%; height: 400px;" />
          </b-card>
        </b-col>
        <b-col xl="5" md="5">
          <b-card no-body>
            <b-table striped hover responsive class="position-relative"
              :current-page="paymentMethodsDistributionTable.currentPage" :per-page="10"
              :items="paymentMethodsDistributionTable.items" :fields="paymentMethodsDistributionTable.fields"
              :sort-by.sync="paymentMethodsDistributionTable.sortBy"
              :sort-desc.sync="paymentMethodsDistributionTable.sortDesc"
              :sort-direction="paymentMethodsDistributionTable.sortDirection"
              :filter="paymentMethodsDistributionTable.filter"
              :filter-included-fields="paymentMethodsDistributionTable.filterOn">
            </b-table>
            <b-card-body class="d-flex justify-content-between flex-wrap pt-0">
              <!-- pagination -->
              <div>
                <b-pagination v-model="paymentMethodsDistributionTable.currentPage"
                  :total-rows="paymentMethodsDistributionTable.totalRows" first-number last-number
                  prev-class="prev-item" next-class="next-item" class="mb-0">
                  <template #prev-text>
                    <feather-icon icon="ChevronLeftIcon" size="18" />
                  </template>
                  <template #next-text>
                    <feather-icon icon="ChevronRightIcon" size="18" />
                  </template>
                </b-pagination>
              </div>
            </b-card-body>
          </b-card>
        </b-col>
      </b-row>
      <b-row>
        <b-col xl="6" md="6">
          <b-card no-body>
            <b-card-header>
              <h4 class="mb-0">
                Payment methods status distribution
                <feather-icon icon="InfoIcon" size="21" class="text-muted cursor-pointer"
                  id="popover-payment-methods-status-distribution" />
              </h4>
              <b-popover target="popover-payment-methods-status-distribution" triggers="hover" placement="bottom">
                <span>Shows breakdown of paid, canceled, and open orders by payment method, with special focus on
                  Vorkasse/Prepayment status</span>
              </b-popover>
              <b-card-text class="font-medium-5 mb-0">
                <b-input-group class="input-group-merge" style="width: 250px;">
                  <b-input-group-prepend is-text>
                    <feather-icon icon="CalendarIcon" />
                  </b-input-group-prepend>
                  <flat-pickr v-model="dateRange" placeholder="Date Range" class="form-control"
                    :config="{ mode: 'range' }" @on-change="changeDateRange" />
                </b-input-group>
              </b-card-text>
            </b-card-header>
            <ECharts :theme="echartsTheme" ref="paymentMethodsStatusDistributionChart"
              :options="paymentMethodsStatusDistributionChart" style="width: 100%; height: 500px;" />
          </b-card>
        </b-col>
        <b-col xl="6" md="6">
          <b-card no-body>
            <b-table striped hover responsive class="position-relative"
              :current-page="paymentMethodsStatusDistributionTable.currentPage" :per-page="10"
              :items="paymentMethodsStatusDistributionTable.items"
              :fields="paymentMethodsStatusDistributionTable.fields"
              :sort-by.sync="paymentMethodsStatusDistributionTable.sortBy"
              :sort-desc.sync="paymentMethodsStatusDistributionTable.sortDesc"
              :sort-direction="paymentMethodsStatusDistributionTable.sortDirection"
              :filter="paymentMethodsStatusDistributionTable.filter"
              :filter-included-fields="paymentMethodsStatusDistributionTable.filterOn">
            </b-table>
            <b-card-body class="d-flex justify-content-between flex-wrap pt-0">
              <!-- pagination -->
              <div>
                <b-pagination v-model="paymentMethodsStatusDistributionTable.currentPage"
                  :total-rows="paymentMethodsStatusDistributionTable.totalRows" first-number last-number
                  prev-class="prev-item" next-class="next-item" class="mb-0">
                  <template #prev-text>
                    <feather-icon icon="ChevronLeftIcon" size="18" />
                  </template>
                  <template #next-text>
                    <feather-icon icon="ChevronRightIcon" size="18" />
                  </template>
                </b-pagination>
              </div>
            </b-card-body>
          </b-card>
        </b-col>
      </b-row>
    </b-overlay>
  </section>
</template>

<script>
import {
  BRow,
  BCol,
  BTable,
  BPagination,
  BCardBody,
  BCardHeader,
  BCardText,
  BPopover,
  BCard,
  BOverlay,
  BForm,
  BFormGroup,
  BInputGroup,
  BInputGroupPrepend,
  BInputGroupAppend,
  BInputGroupText,
  BFormInput,
  BFormCheckbox,
  BButton,
  BModal,
  BBadge,
  VBTooltip,
} from 'bootstrap-vue';
import Ripple from 'vue-ripple-directive';
import vSelect from 'vue-select';
import flatPickr from 'vue-flatpickr-component';
import axios from 'axios';
import ECharts from 'vue-echarts';
import echartsTheme from '@/mixins/echartsTheme';

import ToastificationContent from '@core/components/toastification/ToastificationContent.vue';

import 'echarts/lib/chart/bar';
import 'echarts/lib/chart/line';
import 'echarts/lib/chart/pie';
import 'echarts/lib/component/legend';
import 'echarts/lib/component/tooltip';

const currentDate = new Date();
const twelveMonthsAgo = new Date();
twelveMonthsAgo.setMonth(currentDate.getMonth() - 11);
const formatMonth = (date) => String(date.getMonth() + 1).padStart(2, '0');
const formatDate = (date) => String(date.getDate()).padStart(2, '0');

export default {
  components: {
    BRow,
    BCol,
    BTable,
    BPagination,
    BCard,
    BCardBody,
    BCardHeader,
    BCardText,
    BPopover,
    BOverlay,
    BForm,
    BFormGroup,
    BInputGroup,
    BInputGroupPrepend,
    BInputGroupAppend,
    BInputGroupText,
    BFormInput,
    BFormCheckbox,
    BButton,
    BModal,
    BBadge,
    vSelect,
    flatPickr,
    ToastificationContent,
    ECharts,
  },
  directives: {
    Ripple,
    'b-tooltip': VBTooltip,
  },
  mixins: [echartsTheme],
  data() {
    return {
      showLiqTrend: true,
      showLiqDev: true,
      showInPayments: true,
      showOutPayments: true,
      showOrdersCancellationRate: true,
      bankAccountStatement: 0,
      liquidityBuffer: 0,
      showExpectedOrders: false,
      expectedOrdersPct: 50,
      priorYearTurnover: {},
      liq7DaysTrendWithOrders: 0.0,
      liq14DaysTrendWithOrders: 0.0,
      liq21DaysTrendWithOrders: 0.0,
      liq2MonthsTrendWithOrders: 0.0,
      liq3MonthsTrendWithOrders: 0.0,
      scenario: 'base',
      scenarioLabels: {
        base: 'Base case',
        conservative: 'Conservative — customers pay 7 days late',
        stress: 'Stress — 10 biggest customer payments on hold',
      },
      scenarioHints: {
        base: 'All documents flow at their stated payment term, gross incl. VAT (matching actual bank movements). The monthly VAT remittance to the Finanzamt (German 7/19 % VAT, due on the 10th), the quarterly OSS VAT to the Bundeskasse (EU B2C VAT at foreign rates, due at the end of the month after the quarter) and permanent payments (manual entries) are included as outflows. Supplier bills paid on time; Vorkasse inflows counted at 85%.',
        conservative: 'Same data as Base, but every customer payment (invoices + open orders) arrives 7 days later. Amounts unchanged, so long horizons converge to Base. Supplier bills, VAT/OSS remittance, permanent payments & Vorkasse (85%) unchanged.',
        stress: 'Same data as Base, but the 10 biggest customer payments are removed from every horizon. Supplier bills, VAT/OSS remittance & permanent payments paid on time; Vorkasse inflows counted at 85%.',
      },
      oldDateRange: `${twelveMonthsAgo.getFullYear()}-${formatMonth(twelveMonthsAgo)}-01 to ${currentDate.getFullYear()}-${formatMonth(currentDate)}-${formatDate(currentDate)}`,
      dateRange: `${twelveMonthsAgo.getFullYear()}-${formatMonth(twelveMonthsAgo)}-01 to ${currentDate.getFullYear()}-${formatMonth(currentDate)}-${formatDate(currentDate)}`,
      liq7DaysTrend: 0.0,
      liq14DaysTrend: 0.0,
      liq21DaysTrend: 0.0,
      liq2MonthsTrend: 0.0,
      liq3MonthsTrend: 0.0,
      liqTrendResults: [],
      liqTrendDetails: [],
      cardDetail: {
        show: false,
        title: '',
        rows: [],
        startBalance: 0.0,
        endBalance: 0.0,
        totalIn: 0.0,
        totalOut: 0.0,
        fields: [
          { key: 'payment_term', label: 'Due date', sortable: true },
          { key: 'doc_type', label: 'Type', sortable: true },
          { key: 'doc_no', label: 'Document', sortable: true },
          { key: 'counterparty', label: 'Customer / Supplier', sortable: true },
          { key: 'payment_method', label: 'Payment', sortable: true },
          {
            key: 'gross', label: 'Amount', sortable: true,
            formatter: val => `€ ${this.$formatNumber(val)}`,
          },
          {
            key: 'paid', label: 'Already paid', sortable: true,
            formatter: val => `€ ${this.$formatNumber(val)}`,
          },
          {
            key: 'net', label: 'Net', sortable: true,
            formatter: val => `€ ${this.$formatNumber(val)}`,
          },
          {
            key: 'balance', label: 'Balance', sortable: true,
            formatter: val => `€ ${this.$formatNumber(val)}`,
          },
        ],
      },
      permanentPaymentID: '',
      permanentPaymentTypes: ['once', 'monthly', 'quarterly', 'yearly'],
      permanentPaymentName: '',
      permanentPaymentValue: 0.0,
      permanentPaymentStartDate: '',
      permanentPaymentEndDate: '',
      permanentPaymentDate: '',
      permanentPaymentType: '',
      isAvgPayment: false,
      queryParamsLiqTrend: {},
      queryParams: {},
      queryParamsInPayments: {},
      queryParamsOutPayments: {},
      queryParamsPermanentPayments: {},
      totalInPayments: 0.0,
      totalOutPayments: 0.0,
      liqTrendChart: {
        tooltip: {
          trigger: 'axis',
          borderWidth: 1,
          padding: 12,
          textStyle: {
            fontSize: 13
          },
          extraCssText: 'box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1); border-radius: 8px;',
          axisPointer: {
            type: 'shadow',
            shadowStyle: {
              color: 'rgba(0, 0, 0, 0.05)'
            }
          },
          formatter: function (params) {
            let result = params[0].name + '<br/>';
            params.forEach(param => {
              result += param.value ? `${param.marker} ${param.seriesName}: ${parseFloat(param.value).toLocaleString('de-DE')} €<br/>` : '';
            });
            return result;
          }
        },
        legend: {
          bottom: 0,
          textStyle: {
            fontSize: 12
          },
          selected: { 'Incl. expected orders': false },
        },
        grid: {
          left: '3%',
          right: '4%',
          bottom: '8%',
          top: '3%',
          containLabel: true
        },
        xAxis: {
          type: 'category',
          data: [],
          axisTick: {
            alignWithLabel: true
          },
          axisLabel: {
            fontSize: 12
          }
        },
        yAxis: {
          type: 'value',
          splitLine: {
            lineStyle: {
              color: '#edf2f7',
              type: 'dashed'
            }
          },
          axisTick: {
            show: false
          },
          axisLabel: {
            fontSize: 14,
            formatter: function (value) {
              return value.toLocaleString('de-DE');
            }
          }
        },
        series: [],
        color: ['#667eea', '#764ba2', '#f093fb', '#fccb90', '#48c6ef', '#6f86d6'],
      },
      liqTrendTable: {
        totalRows: 1,
        currentPage: 1,
        sortBy: 'payment_term',
        sortDesc: false,
        sortDirection: 'asc',
        filter: null,
        filterOn: [],
        infoModal: {
          id: 'info-modal',
          title: '',
          content: '',
        },
        fields: [
          {
            key: 'value_in',
            label: 'value in',
            sortable: true,
            formatter: val => {
              if (val !== null) {
                return `€ ${this.$formatNumber(val)}`;
              } else {
                return '';
              }
            },
          },
          {
            key: 'paid_in',
            label: 'paid in',
            sortable: true,
            formatter: val => {
              if (val !== null) {
                return `€ ${this.$formatNumber(val)}`;
              } else {
                return '';
              }
            },
          },
          {
            key: 'value_out',
            label: 'value out',
            sortable: true,
            formatter: val => {
              if (val !== null) {
                return `€ ${this.$formatNumber(val)}`;
              } else {
                return '';
              }
            },
          },
          {
            key: 'paid_out',
            label: 'paid out',
            sortable: true,
            formatter: val => {
              if (val !== null) {
                return `€ ${this.$formatNumber(val)}`;
              } else {
                return '';
              }
            },
          },
          {
            key: 'open_orders_value',
            label: 'open orders',
            sortable: true,
            formatter: val => {
              if (val !== null) {
                return `€ ${this.$formatNumber(val)}`;
              } else {
                return '';
              }
            },
          },
          {
            key: 'paid_open_orders',
            label: 'paid open orders',
            sortable: true,
            formatter: val => {
              if (val !== null) {
                return `€ ${this.$formatNumber(val)}`;
              } else {
                return '';
              }
            },
          },
          {
            key: 'vat_out',
            label: 'VAT out',
            sortable: true,
            formatter: val => {
              if (val !== null) {
                return `€ ${this.$formatNumber(val)}`;
              } else {
                return '';
              }
            },
          },
          {
            key: 'oss_vat_out',
            label: 'OSS VAT out',
            sortable: true,
            formatter: val => (val !== null ? `€ ${this.$formatNumber(val)}` : ''),
          },
          {
            key: 'permanent_out',
            label: 'Permanent out',
            sortable: true,
            formatter: val => (val !== null ? `€ ${this.$formatNumber(val)}` : ''),
          },
          {
            key: 'value',
            label: 'total value',
            sortable: true,
            formatter: val => {
              if (val !== null) {
                return `€ ${this.$formatNumber(val)}`;
              } else {
                return '';
              }
            },
          },
          {
            key: 'payment_term',
            label: 'date',
            sortable: true,
            formatter: value => {
              const new_date = new Date(value);
              if (value) {
                return new_date.toLocaleDateString('de-DE');
              }
              return '';
            },
          },
        ],
        /* eslint-disable global-require */
        items: [],
      },
      liquidityDevelopmentChart: {
        tooltip: {
          trigger: 'axis',
          borderWidth: 1,
          padding: 12,
          textStyle: {
            fontSize: 13
          },
          extraCssText: 'box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1); border-radius: 8px;',
          axisPointer: {
            type: 'shadow',
            shadowStyle: {
              color: 'rgba(0, 0, 0, 0.05)'
            }
          },
          formatter: function (params) {
            let result = params[0].name + '<br/>';
            params.forEach(param => {
              result += param.value ? `${param.marker} ${param.seriesName}: ${parseFloat(param.value).toLocaleString('de-DE')} €<br/>` : '';
            });
            return result;
          }
        },
        legend: {
          bottom: 0,
          textStyle: {
            fontSize: 12
          }
        },
        grid: {
          left: '3%',
          right: '4%',
          bottom: '8%',
          top: '3%',
          containLabel: true
        },
        xAxis: {
          type: 'category',
          data: [],
          axisTick: {
            alignWithLabel: true
          },
          axisLabel: {
            fontSize: 12
          }
        },
        yAxis: {
          type: 'value',
          splitLine: {
            lineStyle: {
              color: '#edf2f7',
              type: 'dashed'
            }
          },
          axisTick: {
            show: false
          },
          axisLabel: {
            fontSize: 14,
            formatter: function (value) {
              return value.toLocaleString('de-DE');
            }
          }
        },
        series: [],
      },
      inPaymentsTable: {
        totalRows: 1,
        currentPage: 1,
        sortBy: 'payment_term',
        sortDesc: false,
        sortDirection: 'asc',
        filter: null,
        filterOn: [],
        infoModal: {
          id: 'info-modal',
          title: '',
          content: '',
        },
        fields: [
          { key: 'order_no', label: 'order no', sortable: true },
          { key: 'invoice_no', label: 'invoice no', sortable: true },
          { key: 'company_name', label: 'company name', sortable: true },
          {
            key: 'total_amount',
            label: 'total amount',
            sortable: true,
            formatter: val => {
              if (val !== null) {
                return `€ ${this.$formatNumber(val)}`;
              } else {
                return '';
              }
            },
          },
          {
            key: 'payment',
            label: 'payment',
            sortable: true,
            formatter: val => {
              if (val !== null) {
                return `€ ${this.$formatNumber(val)}`;
              } else {
                return '';
              }
            },
          },
          {
            key: 'will_receive',
            label: 'will receive',
            sortable: true,
            formatter: val => {
              if (val !== null) {
                return `€ ${this.$formatNumber(val)}`;
              } else {
                return '';
              }
            },
          },
          {
            key: 'payment_term',
            label: 'payment term',
            sortable: true,
            formatter: value => {
              const new_date = new Date(value);
              if (value) {
                return new_date.toLocaleDateString('de-DE');
              }
              return '';
            },
          },
          {
            key: 'shipped_date',
            label: 'shipped date',
            sortable: true,
            formatter: value => {
              const new_date = new Date(value);
              if (value) {
                return new_date.toLocaleDateString('de-DE');
              }
              return '';
            },
          },
        ],
        /* eslint-disable global-require */
        items: [],
      },
      outPaymentsTable: {
        totalRows: 1,
        currentPage: 1,
        sortBy: 'payment_term',
        sortDesc: false,
        sortDirection: 'asc',
        filter: null,
        filterOn: [],
        infoModal: {
          id: 'info-modal',
          title: '',
          content: '',
        },
        fields: [
          { key: 'invoice_no', label: 'invoice no', sortable: true },
          { key: 'company_name', label: 'company name', sortable: true },
          {
            key: 'total_amount',
            label: 'total amount',
            sortable: true,
            formatter: val => {
              if (val !== null) {
                return `€ ${this.$formatNumber(val)}`;
              } else {
                return '';
              }
            },
          },
          {
            key: 'payment',
            label: 'payment',
            sortable: true,
            formatter: val => {
              if (val !== null) {
                return `€ ${this.$formatNumber(val)}`;
              } else {
                return '';
              }
            },
          },
          {
            key: 'need_to_pay',
            label: 'need to pay',
            sortable: true,
            formatter: val => {
              if (val !== null) {
                return `€ ${this.$formatNumber(val)}`;
              } else {
                return '';
              }
            },
          },
          {
            key: 'payment_term',
            label: 'payment term',
            sortable: true,
            formatter: value => {
              const new_date = new Date(value);
              if (value) {
                return new_date.toLocaleDateString('de-DE');
              }
              return '';
            },
          },
          {
            key: 'received_date',
            label: 'received date',
            sortable: true,
            formatter: value => {
              const new_date = new Date(value);
              if (value) {
                return new_date.toLocaleDateString('de-DE');
              }
              return '';
            },
          },
        ],
        /* eslint-disable global-require */
        items: [],
      },
      permanentPaymentsTable: {
        totalRows: 1,
        currentPage: 1,
        sortBy: 'name',
        sortDesc: true,
        sortDirection: 'desc',
        filter: null,
        filterOn: [],
        infoModal: {
          id: 'info-modal',
          title: '',
          content: '',
        },
        fields: [
          { key: 'name', label: 'name', sortable: true },
          {
            key: 'value',
            label: 'total amount',
            sortable: true,
            formatter: val => {
              if (val !== null) {
                return `€ ${this.$formatNumber(val)}`;
              } else {
                return '';
              }
            },
          },
          { key: 'recurring_type', label: 'recurring type', sortable: true },
          {
            key: 'start_date',
            label: 'start date',
            sortable: true,
            formatter: value => {
              const new_date = new Date(value);
              if (value) {
                return new_date.toLocaleDateString('de-DE');
              }
              return '';
            },
          },
          {
            key: 'end_date',
            label: 'end date',
            sortable: true,
            formatter: value => {
              const new_date = new Date(value);
              if (value) {
                return new_date.toLocaleDateString('de-DE');
              }
              return '';
            },
          },
          {
            key: 'payment_date',
            label: 'payment term',
            sortable: true,
            formatter: value => {
              const new_date = new Date(value);
              if (value) {
                return new_date.toLocaleDateString('de-DE');
              }
              return '';
            },
          },
          { key: 'is_avg_payment', label: 'avg. payment', sortable: true },
          { key: 'delete', label: '' },
        ],
        /* eslint-disable global-require */
        items: [],
      },
      ordersCountDevelopmentChart: {
        tooltip: {
          trigger: 'axis',
          borderWidth: 1,
          padding: 12,
          textStyle: {
            fontSize: 13
          },
          extraCssText: 'box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1); border-radius: 8px;',
          axisPointer: {
            type: 'shadow',
            shadowStyle: {
              color: 'rgba(0, 0, 0, 0.05)'
            }
          },
          formatter: function (params) {
            let result = params[0].name + '<br/>';
            params.forEach(param => {
              result += param.value ? `${param.marker} ${param.seriesName}: ${parseFloat(param.value).toLocaleString('de-DE')}<br/>` : '';
            });
            return result;
          }
        },
        legend: {
          bottom: 0,
          textStyle: {
            fontSize: 12
          }
        },
        grid: {
          left: '3%',
          right: '4%',
          bottom: '8%',
          top: '3%',
          containLabel: true
        },
        xAxis: {
          type: 'category',
          data: [],
          axisTick: {
            alignWithLabel: true
          },
          axisLabel: {
            fontSize: 12
          }
        },
        yAxis: {
          type: 'value',
          splitLine: {
            lineStyle: {
              color: '#edf2f7',
              type: 'dashed'
            }
          },
          axisTick: {
            show: false
          },
          axisLabel: {
            fontSize: 14,
            formatter: function (value) {
              return value.toLocaleString('de-DE');
            }
          }
        },
        series: [],
      },
      ordersCountTable: {
        totalRows: 1,
        currentPage: 1,
        sortDesc: false,
        sortDirection: 'asc',
        filter: null,
        filterOn: [],
        infoModal: {
          id: 'info-modal',
          title: '',
          content: '',
        },
        fields: [
          { key: 'orders_count', label: 'orders count', sortable: true },
          { key: 'canceled_orders_count', label: 'canceled orders count', sortable: true },
          { key: 'cancellation_rate', label: 'cancellation rate', sortable: true },
          { key: 'month_year', label: 'date', sortable: true },
        ],
        /* eslint-disable global-require */
        items: [],
      },
      cancellationRateDevelopmentChart: {
        tooltip: {
          trigger: 'axis',
          borderWidth: 1,
          padding: 12,
          textStyle: {
            fontSize: 13
          },
          extraCssText: 'box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1); border-radius: 8px;',
          axisPointer: {
            type: 'shadow',
            shadowStyle: {
              color: 'rgba(0, 0, 0, 0.05)'
            }
          },
          formatter: function (params) {
            let result = params[0].name + '<br/>';
            params.forEach(param => {
              result += param.value ? `${param.marker} ${param.seriesName}: ${parseFloat(param.value).toLocaleString('de-DE')}<br/>` : '';
            });
            return result;
          }
        },
        legend: {
          bottom: 0,
          textStyle: {
            fontSize: 12
          }
        },
        grid: {
          left: '3%',
          right: '4%',
          bottom: '8%',
          top: '3%',
          containLabel: true
        },
        xAxis: {
          type: 'category',
          data: [],
          axisTick: {
            alignWithLabel: true
          },
          axisLabel: {
            fontSize: 12
          }
        },
        yAxis: {
          type: 'value',
          splitLine: {
            lineStyle: {
              color: '#edf2f7',
              type: 'dashed'
            }
          },
          axisTick: {
            show: false
          },
          axisLabel: {
            fontSize: 14,
            formatter: function (value) {
              return value.toLocaleString('de-DE');
            }
          }
        },
        series: [],
      },
      cancellationRateOrderValueDevelopmentChart: {
        tooltip: {
          trigger: 'axis',
          borderWidth: 1,
          padding: 12,
          textStyle: {
            fontSize: 13
          },
          extraCssText: 'box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1); border-radius: 8px;',
          axisPointer: {
            type: 'shadow',
            shadowStyle: {
              color: 'rgba(0, 0, 0, 0.05)'
            }
          },
          formatter: function (params) {
            let result = params[0].name + '<br/>';
            params.forEach(param => {
              result += param.value ? `${param.marker} ${param.seriesName}: ${parseFloat(param.value).toLocaleString('de-DE')}<br/>` : '';
            });
            return result;
          }
        },
        legend: {
          bottom: 0,
          textStyle: {
            fontSize: 12
          }
        },
        grid: {
          left: '3%',
          right: '4%',
          bottom: '8%',
          top: '3%',
          containLabel: true
        },
        xAxis: {
          type: 'category',
          data: [],
          axisTick: {
            alignWithLabel: true
          },
          axisLabel: {
            fontSize: 12
          }
        },
        yAxis: {
          type: 'value',
          splitLine: {
            lineStyle: {
              color: '#edf2f7',
              type: 'dashed'
            }
          },
          axisTick: {
            show: false
          },
          axisLabel: {
            fontSize: 14,
            formatter: function (value) {
              return value.toLocaleString('de-DE');
            }
          }
        },
        series: [],
      },
      canceledProductsTable: {
        totalRows: 1,
        currentPage: 1,
        sortBy: 'qty_cancelled',
        sortDesc: true,
        sortDirection: 'desc',
        filter: null,
        filterOn: [],
        infoModal: {
          id: 'info-modal',
          title: '',
          content: '',
        },
        fields: [
          { key: 'sku', label: 'sku', sortable: true },
          { key: 'name', label: 'name', sortable: true },
          { key: 'qty_cancelled', label: 'qty cancelled', sortable: true },
        ],
        /* eslint-disable global-require */
        items: [],
      },
      avgCancelledOrderValueDevelopmentChart: {
        tooltip: {
          trigger: 'axis',
          borderWidth: 1,
          padding: 12,
          textStyle: {
            fontSize: 13
          },
          extraCssText: 'box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1); border-radius: 8px;',
          axisPointer: {
            type: 'shadow',
            shadowStyle: {
              color: 'rgba(0, 0, 0, 0.05)'
            }
          },
          formatter: function (params) {
            let result = params[0].name + '<br/>';
            params.forEach(param => {
              result += param.value ? `${param.marker} ${param.seriesName}: ${parseFloat(param.value).toLocaleString('de-DE')}<br/>` : '';
            });
            return result;
          }
        },
        legend: {
          bottom: 0,
          textStyle: {
            fontSize: 12
          }
        },
        grid: {
          left: '3%',
          right: '4%',
          bottom: '8%',
          top: '3%',
          containLabel: true
        },
        xAxis: {
          type: 'category',
          data: [],
          axisTick: {
            alignWithLabel: true
          },
          axisLabel: {
            fontSize: 12
          }
        },
        yAxis: {
          type: 'value',
          splitLine: {
            lineStyle: {
              color: '#edf2f7',
              type: 'dashed'
            }
          },
          axisTick: {
            show: false
          },
          axisLabel: {
            fontSize: 14,
            formatter: function (value) {
              return value.toLocaleString('de-DE');
            }
          }
        },
        series: [],
      },
      cancellationRateCustomerGroupDevelopmentChart: {
        tooltip: {
          trigger: 'axis',
          borderWidth: 1,
          padding: 12,
          textStyle: {
            fontSize: 13
          },
          extraCssText: 'box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1); border-radius: 8px;',
          axisPointer: {
            type: 'shadow',
            shadowStyle: {
              color: 'rgba(0, 0, 0, 0.05)'
            }
          },
          formatter: function (params) {
            let result = params[0].name + '<br/>';
            params.forEach(param => {
              result += param.value ? `${param.marker} ${param.seriesName}: ${parseFloat(param.value).toLocaleString('de-DE')}<br/>` : '';
            });
            return result;
          }
        },
        legend: {
          bottom: 0,
          textStyle: {
            fontSize: 12
          }
        },
        grid: {
          left: '3%',
          right: '4%',
          bottom: '8%',
          top: '3%',
          containLabel: true
        },
        xAxis: {
          type: 'category',
          data: [],
          axisTick: {
            alignWithLabel: true
          },
          axisLabel: {
            fontSize: 12
          }
        },
        yAxis: {
          type: 'value',
          splitLine: {
            lineStyle: {
              color: '#edf2f7',
              type: 'dashed'
            }
          },
          axisTick: {
            show: false
          },
          axisLabel: {
            fontSize: 14,
            formatter: function (value) {
              return value.toLocaleString('de-DE');
            }
          }
        },
        series: [],
      },
      paymentMethodsDistributionChart: {
        tooltip: {
          trigger: 'item',
          borderWidth: 1,
          padding: 12,
          textStyle: {
            fontSize: 13
          },
          extraCssText: 'box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1); border-radius: 8px;',
          formatter: function (params) {
            return params.name + '<br/>' + `${params.marker} ${params.seriesName}: ${params.value.toLocaleString('de-DE')}<br/>`;
          }
        },
        legend: {
          orient: 'vertical',
          left: '50%', // Move legend to the right
          top: 'center',
          textStyle: {
            fontSize: 12
          }
        },
        color: ['#667eea', '#764ba2', '#f093fb', '#fccb90', '#d57eeb', '#48c6ef', '#6f86d6', '#a8e6cf', '#ffd3b6', '#ffaaa5'],
        series: [
          {
            name: 'Orders count',
            type: 'pie',
            radius: ['40%', '70%'],
            center: ['25%', '50%'],
            avoidLabelOverlap: false,
            itemStyle: {
              borderRadius: 10,
              borderColor: '#fff',
              borderWidth: 2
            },
            label: {
              show: false,
              position: 'center'
            },
            emphasis: {
              label: {
                show: true,
                fontSize: 20,
                fontWeight: 'bold'
              },
              itemStyle: {
                shadowBlur: 10,
                shadowOffsetX: 0,
                shadowColor: 'rgba(0, 0, 0, 0.5)'
              }
            },
            labelLine: {
              show: false
            },
            data: []
          }
        ],
      },
      paymentMethodsDistributionTable: {
        totalRows: 1,
        currentPage: 1,
        sortBy: 'orders_count',
        sortDesc: true,
        sortDirection: 'desc',
        filter: null,
        filterOn: [],
        infoModal: {
          id: 'info-modal',
          title: '',
          content: '',
        },
        fields: [
          { key: 'payment_method', label: 'payment method', sortable: true },
          { key: 'orders_count', label: 'orders count', sortable: true },
          {
            key: 'turnover',
            label: 'turnover',
            sortable: true,
            formatter: val => {
              if (val !== null) {
                return `€ ${this.$formatNumber(val)}`;
              } else {
                return '';
              }
            },
          },
        ],
        /* eslint-disable global-require */
        items: [],
      },
      paymentMethodsStatusDistributionChart: {
        tooltip: {
          trigger: 'axis',
          borderWidth: 1,
          padding: 12,
          textStyle: {
            fontSize: 13
          },
          extraCssText: 'box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1); border-radius: 8px;',
          axisPointer: {
            type: 'shadow',
            shadowStyle: {
              color: 'rgba(0, 0, 0, 0.05)'
            }
          },
          formatter: function (params) {
            let result = params[0].name + '<br/>';
            params.forEach(param => {
              result += param.value ? `${param.marker} ${param.seriesName}: ${parseFloat(param.value).toLocaleString('de-DE')} €<br/>` : '';
            });
            return result;
          }
        },
        legend: {
          data: ['Paid Value', 'Canceled Value', 'Open Value'],
          bottom: 0,
          textStyle: {
            fontSize: 12
          }
        },
        grid: {
          left: '3%',
          right: '4%',
          bottom: '8%',
          top: '3%',
          containLabel: true
        },
        xAxis: {
          type: 'category',
          data: [],
          axisTick: {
            alignWithLabel: true
          },
          axisLabel: {
            interval: 0,
            rotate: 45,
            fontSize: 11
          }
        },
        yAxis: {
          type: 'value',
          splitLine: {
            lineStyle: {
              color: '#edf2f7',
              type: 'dashed'
            }
          },
          axisTick: {
            show: false
          },
          axisLabel: {
            fontSize: 14,
            formatter: function (value) {
              return value.toLocaleString('de-DE') + ' €';
            }
          }
        },
        series: [
          {
            name: 'Paid Value',
            type: 'bar',
            stack: 'total',
            emphasis: {
              focus: 'series',
              itemStyle: {
                shadowBlur: 10,
                shadowColor: 'rgba(102, 126, 234, 0.3)'
              }
            },
            data: [],
            itemStyle: {
              color: {
                type: 'linear',
                x: 0,
                y: 0,
                x2: 0,
                y2: 1,
                colorStops: [{
                  offset: 0, color: '#667eea'
                }, {
                  offset: 1, color: '#764ba2'
                }]
              },
              borderRadius: [0, 0, 0, 0]
            }
          },
          {
            name: 'Canceled Value',
            type: 'bar',
            stack: 'total',
            emphasis: {
              focus: 'series',
              itemStyle: {
                shadowBlur: 10,
                shadowColor: 'rgba(240, 147, 251, 0.3)'
              }
            },
            data: [],
            itemStyle: {
              color: {
                type: 'linear',
                x: 0,
                y: 0,
                x2: 0,
                y2: 1,
                colorStops: [{
                  offset: 0, color: '#f093fb'
                }, {
                  offset: 1, color: '#d57eeb'
                }]
              },
              borderRadius: [0, 0, 0, 0]
            }
          },
          {
            name: 'Open Value',
            type: 'bar',
            stack: 'total',
            emphasis: {
              focus: 'series',
              itemStyle: {
                shadowBlur: 10,
                shadowColor: 'rgba(252, 203, 144, 0.3)'
              }
            },
            data: [],
            itemStyle: {
              color: {
                type: 'linear',
                x: 0,
                y: 0,
                x2: 0,
                y2: 1,
                colorStops: [{
                  offset: 0, color: '#fccb90'
                }, {
                  offset: 1, color: '#ffd3b6'
                }]
              },
              borderRadius: [4, 4, 0, 0]
            }
          }
        ]
      },
      paymentMethodsStatusDistributionTable: {
        totalRows: 1,
        currentPage: 1,
        sortBy: 'total_turnover',
        sortDesc: true,
        sortDirection: 'desc',
        filter: null,
        filterOn: [],
        infoModal: {
          id: 'info-modal',
          title: '',
          content: '',
        },
        fields: [
          { key: 'payment_method', label: 'Payment Method', sortable: true },
          { key: 'total_orders_count', label: 'Total Orders', sortable: true },
          {
            key: 'total_turnover',
            label: 'Total Value',
            sortable: true,
            formatter: val => {
              if (val !== null) {
                return `€ ${this.$formatNumber(val)}`;
              } else {
                return '';
              }
            },
          },
          { key: 'paid_orders_count', label: 'Paid', sortable: true },
          {
            key: 'paid_turnover',
            label: 'Paid Value',
            sortable: true,
            formatter: val => {
              if (val !== null) {
                return `€ ${this.$formatNumber(val)}`;
              } else {
                return '';
              }
            },
          },
          { key: 'canceled_orders_count', label: 'Canceled', sortable: true },
          {
            key: 'canceled_turnover',
            label: 'Canceled Value',
            sortable: true,
            formatter: val => {
              if (val !== null) {
                return `€ ${this.$formatNumber(val)}`;
              } else {
                return '';
              }
            },
          },
          { key: 'open_orders_count', label: 'Open', sortable: true },
          {
            key: 'open_turnover',
            label: 'Open Value',
            sortable: true,
            formatter: val => {
              if (val !== null) {
                return `€ ${this.$formatNumber(val)}`;
              } else {
                return '';
              }
            },
          },
        ],
        /* eslint-disable global-require */
        items: [],
      },
      paymentDaysDistributionChart: {
        tooltip: {
          trigger: 'axis',
          borderWidth: 1,
          padding: 12,
          textStyle: {
            fontSize: 13
          },
          extraCssText: 'box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1); border-radius: 8px;',
          axisPointer: {
            type: 'shadow',
            shadowStyle: {
              color: 'rgba(0, 0, 0, 0.05)'
            }
          },
          formatter: function (params) {
            let result = params[0].name + '<br/>';
            params.forEach(param => {
              result += param.value ? `${param.marker} ${param.seriesName}: ${parseFloat(param.value).toLocaleString('de-DE')}<br/>` : '';
            });
            return result;
          }
        },
        legend: {
          bottom: 0,
          textStyle: {
            fontSize: 12
          }
        },
        grid: {
          left: '3%',
          right: '4%',
          bottom: '8%',
          top: '3%',
          containLabel: true
        },
        xAxis: {
          type: 'category',
          data: [],
          axisTick: {
            alignWithLabel: true
          },
          axisLabel: {
            fontSize: 12
          }
        },
        yAxis: {
          type: 'value',
          splitLine: {
            lineStyle: {
              color: '#edf2f7',
              type: 'dashed'
            }
          },
          axisTick: {
            show: false
          },
          axisLabel: {
            fontSize: 14,
            formatter: function (value) {
              return value.toLocaleString('de-DE');
            }
          }
        },
        series: [],
      },
    }
  },
  async created() {
    const dateRange = this.dateRange.split(' to ');
    this.queryParams.from_date = dateRange[0];
    this.queryParams.to_date = dateRange[1];

    const savedBuffer = localStorage.getItem('liquidityBuffer');
    if (savedBuffer !== null) {
      this.liquidityBuffer = savedBuffer;
    }
    const savedShowOrders = localStorage.getItem('liquidityShowExpectedOrders');
    if (savedShowOrders !== null) {
      this.showExpectedOrders = savedShowOrders === 'true';
    }
    const savedOrdersPct = localStorage.getItem('liquidityExpectedOrdersPct');
    if (savedOrdersPct !== null) {
      this.expectedOrdersPct = savedOrdersPct;
    }

    try {
      await this.getLiqTrend();
      await this.getLiquidityDevelopment();
      await this.getInPayments();
      await this.getOutPayments();

      this.showOrdersCancellationRate = true;
      await this.getOrdersCancellationRate();
      await this.getCanceledProducts();
      await this.getCancellationRateByCustomerGroup();
      await this.getPaymentMethodsDistribution();
      await this.getPaymentMethodsStatusDistribution();
      await this.getPaymentDaysDistribution();
    } catch (error) {
      if (error.response.status === 401) {
        this.$router.push({ name: 'login' });
      }
    }
  },
  methods: {
    showToast(variant, text) {
      this.$toast({
        component: ToastificationContent,
        props: {
          title: text,
          icon: 'BellIcon',
          variant,
        },
      })
    },
    async makeRequest(url, params) {
      return axios.get(url, {
        headers: {
          Authorization: `JWT ${this.$store.state.jwt}`,
          'Content-Type': 'application/json',
        },
        params,
      });
    },
    async getLiqTrend() {
      this.showLiqTrend = true;
      try {
        const response = await this.makeRequest(`${process.env.VUE_APP_ROOT_API}/liquidity-trend/`, this.queryParamsLiqTrend);
        const results = response.data.results;

        // Sort chronologically so the cumulative running balance is built in date order.
        results.sort((a, b) => new Date(a.payment_term) - new Date(b.payment_term));

        // Keep the sorted rows so the per-card drill-down can rebuild the breakdown.
        this.liqTrendResults = results;

        // Fetch the item-level breakdown (one row per invoice / supplier bill / open
        // order) so each card can drill down to the individual documents.
        try {
          const detResp = await this.makeRequest(`${process.env.VUE_APP_ROOT_API}/liquidity-trend-details/`, this.queryParamsLiqTrend);
          this.liqTrendDetails = (detResp.data.results || [])
            .sort((a, b) => new Date(a.payment_term) - new Date(b.payment_term));
        } catch (detErr) {
          this.liqTrendDetails = [];
          console.error('Failed to load liquidity detail:', detErr);
        }

        this.liqTrendTable.items = results;
        this.liqTrendTable.totalRows = results.length * 2;

        // Last-year monthly turnover, used to overlay an "expected incoming orders"
        // baseline on the forecast (see buildExpectedOverlay / renderScenario).
        await this.getPriorYearTurnover();

        // Build the chart + cards for the currently selected scenario from the
        // item-level detail, so Base / Conservative / Stress all reconcile to the cent.
        this.renderScenario();
      } catch (error) {
        if (error.response && error.response.status === 401) {
          this.$router.push({ name: 'login' });
        } else {
          // Handle other types of errors
          console.error('An error occurred:', error);
        }
      } finally {
        this.showLiqTrend = false;
      }
    },
    async getLiquidityDevelopment() {
      this.showLiqDev = true;
      try {
        const response = await this.makeRequest(`${process.env.VUE_APP_ROOT_API}/liquidity-development/`, this.queryParams);
        this.liquidityDevelopmentChart.xAxis.data = response.data.results.map(item => item.month_year);
        this.liquidityDevelopmentChart.series[0] = {
          name: 'Value',
          type: 'line',
          smooth: true,
          areaStyle: {
            opacity: 0.7,
            color: {
              type: 'linear',
              x: 0,
              y: 0,
              x2: 0,
              y2: 1,
              colorStops: [{
                offset: 0, color: 'rgba(72, 198, 239, 0.5)'
              }, {
                offset: 1, color: 'rgba(111, 134, 214, 0.1)'
              }]
            }
          },
          itemStyle: {
            color: '#48c6ef',
            borderWidth: 2
          },
          lineStyle: {
            width: 3,
            color: {
              type: 'linear',
              x: 0,
              y: 0,
              x2: 1,
              y2: 0,
              colorStops: [{
                offset: 0, color: '#48c6ef'
              }, {
                offset: 1, color: '#6f86d6'
              }]
            }
          },
          label: {
            show: true,
            position: 'top',
            distance: 5,
            align: 'left',
            verticalAlign: 'middle',
            rotate: 90,
            formatter: params => params.value ? params.value.toLocaleString('de-DE') : '',
          },
          data: response.data.results.map(item => item.value),
          markLine: {
            symbol: 'none',
            data: [{ type: 'average', name: 'Avg' }],
            lineStyle: {
              color: '#764ba2',
              type: 'dashed'
            }
          }
        };
      } catch (error) {
        if (error.response && error.response.status === 401) {
          this.$router.push({ name: 'login' });
        } else {
          // Handle other types of errors
          console.error('An error occurred:', error);
        }
      } finally {
        this.showLiqDev = false;
      }
    },
    async getInPayments() {
      this.showInPayments = true;
      try {
        const response = await this.makeRequest(`${process.env.VUE_APP_ROOT_API}/exp-in-payments/`, this.queryParamsInPayments);
        const results = response.data.results;
        if (Array.isArray(results) && results.length > 0) {
          this.inPaymentsTable.items = results;
          this.inPaymentsTable.totalRows = results[0].count * 2;
          this.totalInPayments = parseFloat(results[0].total) || 0.0;
        }
        else {
          this.inPaymentsTable.items = [];
          this.inPaymentsTable.totalRows = 0;
          this.totalInPayments = 0.0;
        }
      } catch (error) {
        if (error.response && error.response.status === 401) {
          this.$router.push({ name: 'login' });
        } else {
          // Handle other types of errors
          console.error('An error occurred:', error);
        }
      } finally {
        this.showInPayments = false;
      }
    },
    async getOutPayments() {
      this.showOutPayments = true;
      try {
        const response = await this.makeRequest(`${process.env.VUE_APP_ROOT_API}/exp-out-payments/`, this.queryParamsOutPayments);
        const results = response.data.results;
        if (Array.isArray(results) && results.length > 0) {
          this.outPaymentsTable.items = results;
          this.outPaymentsTable.totalRows = results[0].count * 2;
          this.totalOutPayments = parseFloat(results[0].total) || 0.0;
        }
        else {
          this.outPaymentsTable.items = [];
          this.outPaymentsTable.totalRows = 0;
          this.totalOutPayments = 0.0;
        }
      } catch (error) {
        if (error.response && error.response.status === 401) {
          this.$router.push({ name: 'login' });
        } else {
          // Handle other types of errors
          console.error('An error occurred:', error);
        }
      } finally {
        this.showOutPayments = false;
      }
    },
    async getPermanentPayments() {
      try {
        const response = await this.makeRequest(`${process.env.VUE_APP_ROOT_API}/permanent-payments/`, this.queryParamsPermanentPayments);
        const results = response.data.results;
        if (Array.isArray(results) && results.length > 0) {
          this.permanentPaymentsTable.items = results;
          this.permanentPaymentsTable.totalRows = response.data.count;
        }
        else {
          this.permanentPaymentsTable.items = [];
          this.permanentPaymentsTable.totalRows = 0;
        }
      } catch (error) {
        if (error.response && error.response.status === 401) {
          this.$router.push({ name: 'login' });
        } else {
          // Handle other types of errors
          console.error('An error occurred:', error);
        }
      }
    },
    async getOrdersCancellationRate() {
      try {
        const response = await this.makeRequest(`${process.env.VUE_APP_ROOT_API}/orders-cancellation-rate/`, this.queryParams);
        const results = response.data.results;
        const monthYearLabels = results.map(item => item.month_year);

        this.ordersCountDevelopmentChart.xAxis.data = monthYearLabels;
        this.ordersCountDevelopmentChart.series[0] = {
          name: 'Canceled orders count',
          type: 'line',
          smooth: true,
          itemStyle: {
            color: '#f093fb',
            borderWidth: 2
          },
          lineStyle: {
            width: 2,
            color: '#f093fb'
          },
          label: {
            show: true,
            position: 'top',
            distance: 5,
            align: 'left',
            verticalAlign: 'middle',
            rotate: 90,
            formatter: params => params.value ? params.value.toLocaleString('de-DE') : '',
          },
          data: results.map(item => item.canceled_orders_count),
          markLine: {
            symbol: 'none',
            data: [{ type: 'average', name: 'Avg' }],
            lineStyle: {
              color: '#764ba2',
              type: 'dashed'
            }
          }
        };
        this.ordersCountDevelopmentChart.series[1] = {
          name: 'Orders count',
          type: 'line',
          smooth: true,
          itemStyle: {
            color: '#667eea',
            borderWidth: 2
          },
          lineStyle: {
            width: 2,
            color: '#667eea'
          },
          label: {
            show: true,
            position: 'top',
            distance: 5,
            align: 'left',
            verticalAlign: 'middle',
            rotate: 90,
            formatter: params => params.value ? params.value.toLocaleString('de-DE') : '',
          },
          data: results.map(item => item.orders_count),
          markLine: {
            symbol: 'none',
            data: [{ type: 'average', name: 'Avg' }],
            lineStyle: {
              color: '#764ba2',
              type: 'dashed'
            }
          }
        };

        this.ordersCountTable.items = results;
        this.ordersCountTable.totalRows = results.length * 2;

        this.cancellationRateDevelopmentChart.xAxis.data = monthYearLabels;
        this.cancellationRateDevelopmentChart.series[0] = {
          name: "Cancellation rate",
          type: 'bar',
          emphasis: {
            focus: 'series'
          },
          label: {
            show: true,
            position: 'inside',
            distance: 5,
            align: 'center',
            verticalAlign: 'middle',
            rotate: 90,
            formatter: params => params.value ? params.value.toLocaleString('de-DE') : '',
            fontSize: 14,
            fontWeight: 'bold',
            color: '#000',
          },
          data: results.map(item => item.cancellation_rate),
          itemStyle: {
            color: {
              type: 'linear',
              x: 0,
              y: 1,
              x2: 0,
              y2: 0,
              colorStops: [{
                offset: 0, color: '#fccb90'
              }, {
                offset: 1, color: '#d57eeb'
              }]
            }
          },
          markLine: {
            symbol: 'none',
            data: [{ type: 'average', name: 'Avg' }],
            lineStyle: {
              color: '#764ba2',
              type: 'dashed'
            }
          }
        };

        this.cancellationRateOrderValueDevelopmentChart.xAxis.data = monthYearLabels;
        this.cancellationRateOrderValueDevelopmentChart.series[0] = {
          name: "Cancellation rate",
          type: 'bar',
          emphasis: {
            focus: 'series'
          },
          label: {
            show: true,
            position: 'inside',
            distance: 5,
            align: 'center',
            verticalAlign: 'middle',
            rotate: 90,
            formatter: params => params.value ? params.value.toLocaleString('de-DE') : '',
            fontSize: 14,
            fontWeight: 'bold',
            color: '#000',
          },
          data: results.map(item => item.cancellation_rate_order_value),
          itemStyle: {
            color: {
              type: 'linear',
              x: 0,
              y: 1,
              x2: 0,
              y2: 0,
              colorStops: [{
                offset: 0, color: '#48c6ef'
              }, {
                offset: 1, color: '#6f86d6'
              }]
            }
          },
          markLine: {
            symbol: 'none',
            data: [{ type: 'average', name: 'Avg' }],
            lineStyle: {
              color: '#764ba2',
              type: 'dashed'
            }
          }
        };

        this.avgCancelledOrderValueDevelopmentChart.xAxis.data = monthYearLabels;
        this.avgCancelledOrderValueDevelopmentChart.series[0] = {
          name: "Avg. order value",
          type: 'bar',
          emphasis: {
            focus: 'series'
          },
          label: {
            show: true,
            position: 'inside',
            distance: 5,
            align: 'center',
            verticalAlign: 'middle',
            rotate: 90,
            formatter: params => params.value ? params.value.toLocaleString('de-DE') : '',
            fontSize: 14,
            fontWeight: 'bold',
            color: '#000',
          },
          data: results.map(item => item.canceled_avg_order_value),
          itemStyle: {
            color: {
              type: 'linear',
              x: 0,
              y: 1,
              x2: 0,
              y2: 0,
              colorStops: [{
                offset: 0, color: '#764ba2'
              }, {
                offset: 1, color: '#f093fb'
              }]
            }
          },
          markLine: {
            symbol: 'none',
            data: [{ type: 'average', name: 'Avg' }],
            lineStyle: {
              color: '#667eea',
              type: 'dashed'
            }
          }
        };

      } catch (error) {
        if (error.response && error.response.status === 401) {
          this.$router.push({ name: 'login' });
        } else {
          // Handle other types of errors
          console.error('An error occurred:', error);
        }
      }
    },
    async getCanceledProducts() {
      try {
        const response = await this.makeRequest(`${process.env.VUE_APP_ROOT_API}/canceled-products-count/`, this.queryParams);
        const results = response.data.results;

        if (Array.isArray(results) && results.length > 0) {
          this.canceledProductsTable.items = results;
          this.canceledProductsTable.totalRows = results.length * 2;
        }
        else {
          this.canceledProductsTable.items = [];
          this.canceledProductsTable.totalRows = 0;
        }
      } catch (error) {
        if (error.response && error.response.status === 401) {
          this.$router.push({ name: 'login' });
        } else {
          // Handle other types of errors
          console.error('An error occurred:', error);
        }
      }
    },
    async getCancellationRateByCustomerGroup() {
      try {
        const response = await this.makeRequest(`${process.env.VUE_APP_ROOT_API}/cancellation-rate-by-customer-group/`, this.queryParams);
        const results = response.data.results;
        // 1. Get unique month_year values
        const monthYearLabels = [...new Set(results.map(item => item.month_year))];

        // 2. Group results by customer_group
        const groupedByCustomerGroup = results.reduce((acc, curr) => {
          const key = curr.customer_group;
          if (!acc[key]) {
            acc[key] = [];
          }
          acc[key].push(curr);
          return acc;
        }, {});

        // 3. Create a new object with only the groups we care about
        const groupsToInclude = ["A-Kunde", "B-Kunde", "C-Kunde"];
        const newGroupedByCustomerGroup = {};
        groupsToInclude.forEach(group => {
          newGroupedByCustomerGroup[group] = groupedByCustomerGroup[group] || [];
        });

        // 4. Build the datasets for each customer group
        let cancellationRateByCustomerGroupDatasets = [];
        for (const key in newGroupedByCustomerGroup) {
          const groupData = newGroupedByCustomerGroup[key];

          // Create a lookup mapping from month_year to cancellation_rate
          // (Assuming one value per month; if there are multiples, adjust as needed)
          const lookup = groupData.reduce((obj, item) => {
            obj[item.month_year] = item.cancellation_rate;
            return obj;
          }, {});

          // For each distinct month-year label, get the corresponding cancellation_rate or 0.0 if missing.
          const cancellationRateData = monthYearLabels.map(label => (lookup[label] !== undefined ? lookup[label] : 0.0));

          const customerGroupColors = {
            'A-Kunde': {
              type: 'linear',
              x: 0,
              y: 1,
              x2: 0,
              y2: 0,
              colorStops: [{
                offset: 0, color: '#667eea'
              }, {
                offset: 1, color: '#764ba2'
              }]
            },
            'B-Kunde': {
              type: 'linear',
              x: 0,
              y: 1,
              x2: 0,
              y2: 0,
              colorStops: [{
                offset: 0, color: '#48c6ef'
              }, {
                offset: 1, color: '#6f86d6'
              }]
            },
            'C-Kunde': {
              type: 'linear',
              x: 0,
              y: 1,
              x2: 0,
              y2: 0,
              colorStops: [{
                offset: 0, color: '#f093fb'
              }, {
                offset: 1, color: '#d57eeb'
              }]
            }
          };

          cancellationRateByCustomerGroupDatasets.push({
            name: key,
            type: 'bar',
            emphasis: {
              focus: 'series'
            },
            itemStyle: {
              color: customerGroupColors[key] || '#fccb90'
            },
            label: {
              show: true,
              position: 'inside',
              distance: 5,
              align: 'center',
              verticalAlign: 'middle',
              rotate: 90,
              formatter: params => params.value ? params.value.toLocaleString('de-DE') : '',
              fontSize: 14,
              fontWeight: 'bold',
              color: '#000',
            },
            data: cancellationRateData,
          });
        }

        // Finally, assign the xAxis data and series to your chart configuration
        this.cancellationRateCustomerGroupDevelopmentChart.xAxis.data = monthYearLabels;
        this.cancellationRateCustomerGroupDevelopmentChart.series = cancellationRateByCustomerGroupDatasets;

      } catch (error) {
        if (error.response && error.response.status === 401) {
          this.$router.push({ name: 'login' });
        } else {
          // Handle other types of errors
          console.error('An error occurred:', error);
        }
      }
    },
    async getPaymentMethodsDistribution() {
      try {
        const response = await this.makeRequest(`${process.env.VUE_APP_ROOT_API}/payment-methods-distribution/`, this.queryParams);
        const results = response.data.results;

        this.paymentMethodsDistributionChart.series[0].data = results.map(item => {
          return {
            value: item.orders_count,
            name: item.payment_method,
          };
        });

        this.paymentMethodsDistributionTable.items = results;
        this.paymentMethodsDistributionTable.totalRows = results.length * 2;
      } catch (error) {
        if (error.response && error.response.status === 401) {
          this.$router.push({ name: 'login' });
        } else {
          // Handle other types of errors
          console.error('An error occurred:', error);
        }
      }
    },
    async getPaymentMethodsStatusDistribution() {
      try {
        const response = await this.makeRequest(`${process.env.VUE_APP_ROOT_API}/payment-methods-status-distribution/`, this.queryParams);
        const results = response.data.results;

        // Update chart data
        const paymentMethods = results.map(item => item.payment_method);
        this.paymentMethodsStatusDistributionChart.xAxis.data = paymentMethods;

        // Update series data to use turnover instead of counts
        this.paymentMethodsStatusDistributionChart.series[0].data = results.map(item => parseFloat(item.paid_turnover) || 0);
        this.paymentMethodsStatusDistributionChart.series[1].data = results.map(item => parseFloat(item.canceled_turnover) || 0);
        this.paymentMethodsStatusDistributionChart.series[2].data = results.map(item => parseFloat(item.open_turnover) || 0);

        // Update table data
        this.paymentMethodsStatusDistributionTable.items = results;
        this.paymentMethodsStatusDistributionTable.totalRows = results.length * 2;
      } catch (error) {
        if (error.response && error.response.status === 401) {
          this.$router.push({ name: 'login' });
        } else {
          // Handle other types of errors
          console.error('An error occurred:', error);
        }
      }
    },
    async getPaymentDaysDistribution() {
      try {
        const response = await this.makeRequest(`${process.env.VUE_APP_ROOT_API}/payment-days-distribution/`, this.queryParams);
        const results = response.data.results;
        const monthYearLabels = results.map(item => item.month_year);

        this.paymentDaysDistributionChart.xAxis.data = monthYearLabels;
        this.paymentDaysDistributionChart.series[0] = {
          name: "Avg. value",
          type: 'bar',
          emphasis: {
            focus: 'series'
          },
          label: {
            show: true,
            position: 'inside',
            distance: 5,
            align: 'center',
            verticalAlign: 'middle',
            rotate: 90,
            formatter: params => params.value ? params.value.toLocaleString('de-DE') : '',
            fontSize: 14,
            fontWeight: 'bold',
            color: '#000',
          },
          data: results.map(item => item.avg_days_diff),
          itemStyle: {
            color: {
              type: 'linear',
              x: 0,
              y: 1,
              x2: 0,
              y2: 0,
              colorStops: [{
                offset: 0, color: '#a8e6cf'
              }, {
                offset: 1, color: '#48c6ef'
              }]
            },
            borderRadius: [4, 4, 0, 0]
          },
          markLine: {
            symbol: 'none',
            data: [{ type: 'average', name: 'Avg' }],
            lineStyle: {
              color: '#667eea',
              type: 'dashed'
            }
          }
        };
      } catch (error) {
        if (error.response && error.response.status === 401) {
          this.$router.push({ name: 'login' });
        } else {
          // Handle other types of errors
          console.error('An error occurred:', error);
        }
      }
      finally {
        this.showOrdersCancellationRate = false;
      }
    },
    async sortChangedInPayments(value) {
      if (value.sortDesc === true) {
        this.queryParamsInPayments.ordering = `-${value.sortBy}`;
      } else {
        this.queryParamsInPayments.ordering = value.sortBy;
      }
      await this.getInPayments();
    },
    async sortChangedOutPayments(value) {
      if (value.sortDesc === true) {
        this.queryParamsOutPayments.ordering = `-${value.sortBy}`;
      } else {
        this.queryParamsOutPayments.ordering = value.sortBy;
      }
      await this.getOutPayments();
    },
    async handleInPaymentsTablePageChange(value) {
      this.queryParamsInPayments.page = value;
      await this.getInPayments();
    },
    async handleOutPaymentsTablePageChange(value) {
      this.queryParamsOutPayments.page = value;
      await this.getOutPayments();
    },
    async handlePermanentPaymentsPageChange(value) {
      this.queryParamsPermanentPayments.page = value;
      await this.getPermanentPayments();
    },
    addPermanentPaymentClicked() {
      this.$refs['modal-permanent-payment-submit'].show();
    },
    permanentPaymentsRowClicked(row) {
      this.permanentPaymentID = row.id;
      this.permanentPaymentName = row.name;
      this.permanentPaymentValue = row.value;
      this.permanentPaymentStartDate = row.start_date;
      this.permanentPaymentEndDate = row.end_date;
      this.permanentPaymentDate = row.payment_date;
      this.permanentPaymentType = row.recurring_type;
      this.isAvgPayment = row.is_avg_payment;
      this.$refs['modal-permanent-payment-submit'].show();
    },
    resetPermanentPaymentModal() {
      this.permanentPaymentID = '';
      this.permanentPaymentName = '';
      this.permanentPaymentValue = 0.0;
      this.permanentPaymentStartDate = '';
      this.permanentPaymentEndDate = '';
      this.permanentPaymentDate = '';
      this.permanentPaymentType = '';
      this.isAvgPayment = false;
    },
    async handlePermanentPaymentSubmit() {
      const params = {
        name: this.permanentPaymentName,
        value: this.permanentPaymentValue,
        start_date: this.permanentPaymentStartDate,
        end_date: this.permanentPaymentEndDate,
        payment_date: this.permanentPaymentDate,
        recurring_type: this.permanentPaymentType,
        is_avg_payment: this.isAvgPayment,
      }

      if (this.permanentPaymentID
        && this.planNumbersName !== ''
        && this.permanentPaymentValue !== 0.0
        && this.permanentPaymentStartDate
        && this.permanentPaymentEndDate
        && this.permanentPaymentDate
        && this.permanentPaymentType !== '') {
        await axios
          .put(`${process.env.VUE_APP_ROOT_API}/permanent-payments/${this.permanentPaymentID}/`, params, {
            headers: {
              Authorization: `JWT ${this.$store.state.jwt}`,
              'Content-Type': 'application/json',
            },
          })
          .then(async response => {
            console.log(response);
            this.showToast('success', 'Permanent payment has been updated');
            await this.getPermanentPayments();
            await this.getLiqTrend()
          })
          .catch(error => {
            if (error.response.status === 401) {
              this.$router.push({ name: 'login' });
            }
          })
      } else if (this.planNumbersName !== ''
        && this.permanentPaymentValue !== 0.0
        && this.permanentPaymentStartDate
        && this.permanentPaymentEndDate
        && this.permanentPaymentDate
        && this.permanentPaymentType !== '') {
        await axios
          .post(`${process.env.VUE_APP_ROOT_API}/permanent-payments/`, params, {
            headers: {
              Authorization: `JWT ${this.$store.state.jwt}`,
              'Content-Type': 'application/json',
            },
          })
          .then(async response => {
            console.log(response);
            this.showToast('success', 'Permanent payment created');
            await this.getPermanentPayments();
            await this.getLiqTrend()
          })
          .catch(error => {
            if (error.response.status === 401) {
              this.$router.push({ name: 'login' });
            }
          })
      } else { this.showToast('danger', 'Error!') }
    },
    openDeleteConfirm(id) {
      this.permanentPaymentID = id;
      this.$swal({
        title: 'Are you sure?',
        text: "You won't be able to revert this!",
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: 'Yes, delete!',
        customClass: {
          confirmButton: 'btn btn-primary',
          cancelButton: 'btn btn-outline-danger ml-1',
        },
        buttonsStyling: false,
      }).then(async result => {
        if (result.value) {
          await axios
            .delete(`${process.env.VUE_APP_ROOT_API}/permanent-payments/${this.permanentPaymentID}/`, {
              headers: {
                Authorization: `JWT ${this.$store.state.jwt}`,
                'Content-Type': 'application/json',
              },
            })
            .then(async response => {
              console.log(response);
              this.showToast('info', 'Permanent payment has been deleted');
              await this.getPermanentPayments();
              await this.getLiqTrend()
            })
            .catch(error => {
              if (error.response.status === 401) {
                this.$router.push({ name: 'login' });
              }
            })
        }
        this.permanentPaymentID = '';
      })
    },
    cutoffDate(amount, unit) {
      // Horizon end date for a card: `amount` days or months from today.
      const cutoff = new Date();
      if (unit === 'months') {
        cutoff.setMonth(cutoff.getMonth() + amount);
      } else {
        cutoff.setDate(cutoff.getDate() + amount);
      }
      return cutoff;
    },
    shiftDateStr(dateStr, days) {
      const d = new Date(dateStr);
      d.setDate(d.getDate() + days);
      const y = d.getFullYear();
      const m = String(d.getMonth() + 1).padStart(2, '0');
      const day = String(d.getDate()).padStart(2, '0');
      return `${y}-${m}-${day}`;
    },
    isInflowRow(row) {
      return row.doc_type === 'Incoming invoice' || row.doc_type === 'Open order';
    },
    scenarioTransform(rows, scenario) {
      // Apply the scenario assumption to the item-level rows, returning a NEW list
      // (originals untouched). Only customer inflows are affected; supplier bills never.
      // - base:         no change
      // - conservative: every customer payment arrives 7 days later (timing only — so
      //                 long horizons converge to base, since the money still lands)
      // - stress:       the 10 biggest customer payments are put ON HOLD (removed from
      //                 the forecast), which lowers every horizon including 2/3 months
      if (!Array.isArray(rows) || scenario === 'base') {
        return Array.isArray(rows) ? rows.slice() : [];
      }
      if (scenario === 'conservative') {
        return rows.map(r => (this.isInflowRow(r)
          ? { ...r, payment_term: this.shiftDateStr(r.payment_term, 7) }
          : r));
      }
      if (scenario === 'stress') {
        const onHold = new Set(
          rows.filter(this.isInflowRow)
            .slice()
            .sort((a, b) => parseFloat(b.net) - parseFloat(a.net))
            .slice(0, 10),
        );
        return rows.filter(r => !onHold.has(r));
      }
      return rows.slice();
    },
    setScenario(scenario) {
      this.scenario = scenario;
      this.renderScenario();
    },
    renderScenario() {
      // Recompute the chart + the five horizon cards for the active scenario,
      // built from the (scenario-shifted) item-level detail rows.
      const rows = this.scenarioTransform(this.liqTrendDetails, this.scenario);
      const bank = parseFloat(this.bankAccountStatement) || 0.0;

      // Aggregate net per due date, then build the cumulative running balance.
      const byDate = {};
      rows.forEach(r => {
        const d = (r.payment_term || '').slice(0, 10);
        if (!d) { return; }
        byDate[d] = (byDate[d] || 0.0) + parseFloat(r.net);
      });
      // Expected-incoming-orders overlay (synthetic future inflows, netted against
      // orders already booked). Empty when the feature is toggled off.
      const overlay = this.buildExpectedOverlay();

      // X-axis = committed due dates, plus overlay-only future dates so the overlay
      // line can extend past the last booked document.
      const dateSet = {};
      Object.keys(byDate).forEach(d => { dateSet[d] = true; });
      if (this.showExpectedOrders) {
        Object.keys(overlay).forEach(d => { dateSet[d] = true; });
      }
      const dates = Object.keys(dateSet).sort();

      // Two cumulative lines over the same axis: committed only, and incl. expected
      // orders. The committed line carries forward flat across overlay-only dates.
      let running = bank;
      let runningWith = bank;
      const committedData = [];
      const withOrdersData = [];
      dates.forEach(d => {
        running += (byDate[d] || 0.0);
        runningWith += (byDate[d] || 0.0) + (overlay[d] || 0.0);
        committedData.push(running.toFixed(2));
        withOrdersData.push(runningWith.toFixed(2));
      });

      this.liqTrendChart.xAxis.data = dates;
      const committedSeries = {
        name: 'Value',
        type: 'line',
        smooth: true,
        areaStyle: {
          opacity: 0.8,
          color: {
            type: 'linear', x: 0, y: 0, x2: 0, y2: 1,
            colorStops: [
              { offset: 0, color: 'rgba(102, 126, 234, 0.5)' },
              { offset: 1, color: 'rgba(118, 75, 162, 0.1)' },
            ],
          },
        },
        itemStyle: { color: '#667eea', borderWidth: 2 },
        lineStyle: {
          width: 3,
          color: {
            type: 'linear', x: 0, y: 0, x2: 1, y2: 0,
            colorStops: [
              { offset: 0, color: '#667eea' },
              { offset: 1, color: '#764ba2' },
            ],
          },
        },
        label: {
          show: true,
          position: 'top',
          distance: 5,
          align: 'left',
          verticalAlign: 'middle',
          rotate: 90,
          formatter: params => (params.value ? params.value.toLocaleString('de-DE') : ''),
        },
        data: committedData,
        markLine: {
          silent: true,
          symbol: 'none',
          data: [{ yAxis: parseFloat(this.liquidityBuffer) || 0.0, name: 'Buffer' }],
          lineStyle: { color: '#ea5455', type: 'dashed', width: 2 },
          label: {
            show: true,
            position: 'insideEndTop',
            color: '#ea5455',
            formatter: params => `Buffer: € ${Number(params.value).toLocaleString('de-DE')}`,
          },
        },
      };

      // ECharts merges options — once series[1] exists it's never removed by setOption.
      // Fix: always keep both series; control visibility via lineStyle.width and opacity.
      const overlayVisible = this.showExpectedOrders;
      this.liqTrendChart.series.splice(0, this.liqTrendChart.series.length,
        committedSeries,
        {
          name: 'Incl. expected orders',
          type: 'line',
          smooth: true,
          symbol: 'none',
          itemStyle: { color: '#00cfe8', opacity: overlayVisible ? 1 : 0 },
          lineStyle: { width: overlayVisible ? 2 : 0, color: '#00cfe8', type: 'dashed' },
          data: withOrdersData,
        },
      );
      // Sync legend selection so the legend item disappears when the overlay is off.
      this.liqTrendChart.legend.selected = { 'Incl. expected orders': overlayVisible };

      // Cards = bank + cumulative net of all rows due on/before each horizon cutoff.
      const cut7 = this.cutoffDate(7, 'days');
      const cut14 = this.cutoffDate(14, 'days');
      const cut21 = this.cutoffDate(21, 'days');
      const cut2m = this.cutoffDate(2, 'months');
      const cut3m = this.cutoffDate(3, 'months');
      let s7 = 0.0; let s14 = 0.0; let s21 = 0.0; let s2m = 0.0; let s3m = 0.0;
      rows.forEach(r => {
        const cd = new Date(r.payment_term);
        const v = parseFloat(r.net);
        if (cd <= cut7) { s7 += v; }
        if (cd <= cut14) { s14 += v; }
        if (cd <= cut21) { s21 += v; }
        if (cd <= cut2m) { s2m += v; }
        if (cd <= cut3m) { s3m += v; }
      });
      this.liq7DaysTrend = s7 + bank;
      this.liq14DaysTrend = s14 + bank;
      this.liq21DaysTrend = s21 + bank;
      this.liq2MonthsTrend = s2m + bank;
      this.liq3MonthsTrend = s3m + bank;

      // Add the expected-orders overlay due on/before each cutoff to get the
      // "incl. expected orders" figure shown under each card.
      let o7 = 0.0; let o14 = 0.0; let o21 = 0.0; let o2m = 0.0; let o3m = 0.0;
      Object.keys(overlay).forEach(d => {
        const cd = new Date(d);
        const v = overlay[d];
        if (cd <= cut7) { o7 += v; }
        if (cd <= cut14) { o14 += v; }
        if (cd <= cut21) { o21 += v; }
        if (cd <= cut2m) { o2m += v; }
        if (cd <= cut3m) { o3m += v; }
      });
      this.liq7DaysTrendWithOrders = this.liq7DaysTrend + o7;
      this.liq14DaysTrendWithOrders = this.liq14DaysTrend + o14;
      this.liq21DaysTrendWithOrders = this.liq21DaysTrend + o21;
      this.liq2MonthsTrendWithOrders = this.liq2MonthsTrend + o2m;
      this.liq3MonthsTrendWithOrders = this.liq3MonthsTrend + o3m;
    },
    async getPriorYearTurnover() {
      // Fetch last year's invoice turnover per month so the forecast can overlay an
      // estimated "expected incoming orders" inflow (a % of the same month last year).
      const now = new Date();
      const y = now.getFullYear();
      const mm = String(now.getMonth() + 1).padStart(2, '0');
      // From the current month a year ago through the end of this year — covers every
      // future month the trend can reach (the trend caps at next year).
      const fromDate = `${y - 1}-${mm}-01`;
      const toDate = `${y}-12-31`;
      try {
        const resp = await this.makeRequest(
          `${process.env.VUE_APP_ROOT_API}/prior-year-turnover/`,
          { from_date: fromDate, to_date: toDate },
        );
        const map = {};
        (resp.data.results || []).forEach(r => {
          // Key by the target month THIS year (returned year + 1) so a future month
          // maps to its same-month-last-year turnover.
          map[`${parseInt(r.year, 10) + 1}-${parseInt(r.month, 10)}`] = parseFloat(r.turnover) || 0.0;
        });
        this.priorYearTurnover = map;
      } catch (err) {
        this.priorYearTurnover = {};
        console.error('Failed to load prior-year turnover:', err);
      }
    },
    buildExpectedOverlay() {
      // Returns { 'YYYY-MM-DD': amount } of synthetic future inflows representing
      // expected incoming orders: for each future month, expectedOrdersPct % of the
      // same month last year, MINUS orders already booked that month (no double
      // counting), spread evenly across the month's remaining days.
      const overlay = {};
      const pct = parseFloat(this.expectedOrdersPct) / 100.0;
      if (!this.showExpectedOrders || !(pct > 0)) {
        return overlay;
      }

      const today = new Date();
      today.setHours(0, 0, 0, 0);

      // Horizon end = the later of +3 months (longest card) and the last booked
      // payment date, so the overlay line spans the whole chart.
      let end = this.cutoffDate(3, 'months');
      this.liqTrendDetails.forEach(r => {
        const d = new Date(r.payment_term);
        if (d > end) { end = d; }
      });

      // Orders already booked (in the pipeline), summed per 'YYYY-M' month.
      // Use scenario-transformed rows so Conservative/Stress shift or drop booked
      // inflows consistently with the committed line.
      const bookedByMonth = {};
      const scenarioRows = this.scenarioTransform(this.liqTrendDetails, this.scenario);
      scenarioRows.forEach(r => {
        if (!this.isInflowRow(r)) { return; }
        const d = new Date(r.payment_term);
        const key = `${d.getFullYear()}-${d.getMonth() + 1}`;
        bookedByMonth[key] = (bookedByMonth[key] || 0.0) + parseFloat(r.net);
      });

      // Walk each month from the current month to the horizon end.
      const cursor = new Date(today.getFullYear(), today.getMonth(), 1);
      while (cursor <= end) {
        const cy = cursor.getFullYear();
        const cm = cursor.getMonth(); // 0-based
        const key = `${cy}-${cm + 1}`;
        const baseline = pct * (this.priorYearTurnover[key] || 0.0);
        if (baseline > 0) {
          const booked = bookedByMonth[key] || 0.0;
          const net = Math.max(0.0, baseline - booked);
          if (net > 0) {
            const daysInMonth = new Date(cy, cm + 1, 0).getDate();
            const startDay = (cy === today.getFullYear() && cm === today.getMonth())
              ? today.getDate() : 1;
            const nDays = daysInMonth - startDay + 1;
            if (nDays > 0) {
              const perDay = net / nDays;
              for (let day = startDay; day <= daysInMonth; day += 1) {
                const ds = `${cy}-${String(cm + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
                overlay[ds] = (overlay[ds] || 0.0) + perDay;
              }
            }
          }
        }
        cursor.setMonth(cursor.getMonth() + 1);
      }
      return overlay;
    },
    toggleExpectedOrders() {
      // Persist the toggle and redraw (overlay line + card figures appear/disappear).
      this.$nextTick(() => {
        localStorage.setItem('liquidityShowExpectedOrders', this.showExpectedOrders);
        this.renderScenario();
      });
    },
    changeExpectedOrdersPct() {
      localStorage.setItem('liquidityExpectedOrdersPct', this.expectedOrdersPct);
      this.renderScenario();
    },
    showCardDetail(amount, unit, title) {
      // Rebuild exactly what feeds the card: the individual documents (invoices,
      // supplier bills, open orders) due within the horizon, with a running balance
      // that starts from the entered bank statement. The final balance equals the card.
      const cutoff = this.cutoffDate(amount, unit);

      const startBalance = parseFloat(this.bankAccountStatement) || 0.0;
      let running = startBalance;
      let totalIn = 0.0;
      let totalOut = 0.0;

      // Use the same scenario-shifted rows that feed the card, re-sorted by the
      // (possibly shifted) due date, so the drill-down matches the card exactly.
      const rows = this.scenarioTransform(this.liqTrendDetails, this.scenario)
        .filter(item => new Date(item.payment_term) <= cutoff)
        .sort((a, b) => new Date(a.payment_term) - new Date(b.payment_term))
        .map(item => {
          const net = parseFloat(item.net);
          running += net;
          if (net >= 0) {
            totalIn += net;
          } else {
            totalOut += net;
          }
          return { ...item, net, balance: running };
        });

      const scenarioLabel = this.scenarioLabels[this.scenario] || '';
      this.cardDetail.title = `${title} — ${scenarioLabel}`;
      this.cardDetail.rows = rows;
      this.cardDetail.startBalance = startBalance;
      this.cardDetail.endBalance = running;
      this.cardDetail.totalIn = totalIn;
      this.cardDetail.totalOut = totalOut;
      this.cardDetail.show = true;
    },
    async changeBankAccountStatement() {
      await this.getLiqTrend();
    },
    async changeBuffer() {
      // Persist the threshold so it survives reloads, then redraw the chart so the
      // buffer marker line moves to the new level. Card badges update reactively.
      localStorage.setItem('liquidityBuffer', this.liquidityBuffer);
      await this.getLiqTrend();
    },
    isBelowBuffer(value) {
      return parseFloat(value) < (parseFloat(this.liquidityBuffer) || 0.0);
    },
    async changeDateRange() {
      const dateRange = this.dateRange.split(' to ');
      if (dateRange.length > 1 && this.dateRange != this.oldDateRange) {
        this.queryParams.from_date = dateRange[0];
        this.queryParams.to_date = dateRange[1];
        await this.getLiquidityDevelopment();

        this.showOrdersCancellationRate = true;
        await this.getOrdersCancellationRate();
        await this.getCanceledProducts();
        await this.getCancellationRateByCustomerGroup();
        await this.getPaymentMethodsDistribution();
        await this.getPaymentMethodsStatusDistribution();
        await this.getPaymentDaysDistribution();
        this.oldDateRange = this.dateRange;

        this.$nextTick(() => {
          this.$refs.liquidityDevelopmentChart.refresh();
          this.$refs.ordersCountDevelopmentChart.refresh();
          this.$refs.cancellationRateDevelopmentChart.refresh();
          this.$refs.cancellationRateOrderValueDevelopmentChart.refresh();
          this.$refs.cancellationRateCustomerGroupDevelopmentChart.refresh();
          this.$refs.paymentMethodsDistributionChart.refresh();
          this.$refs.paymentMethodsStatusDistributionChart.refresh();
          this.$refs.paymentDaysDistributionChart.refresh();
        });
      }
    },
    async openPermanentPaymentsDetails() {
      await this.getPermanentPayments();
      this.$refs['permanent-payments-modal'].show();
    },
  },
}
</script>

<style lang="scss">
@import "@core/scss/vue/libs/vue-sweetalert.scss";
@import "@core/scss/vue/libs/vue-select.scss";
@import "@core/scss/vue/libs/vue-flatpicker.scss";

.scenario-bar {
  padding: 0.6rem 1rem;
  background: #f8f8fc;
  border: 1px solid #ebe9f1;
  border-radius: 0.5rem;
}
</style>
